// All new components must import useTranslation and use t() for UI strings
export const en = {
  sidebar: {
    portfolio: "Project Portfolio",
    projectList: "Projects",
    projectSchedule: "Project Schedule",
    directory: "Directory",

    invoiceInbox: "Invoice Inbox",
    teamSchedule: "Crew Schedule",
    backToProjectList: "Project List",
    construction_cost: "Financial Overview",
    contracts: "Contracts",
    changeOrders: "Change Orders",
    extrasCredits: "Directives & Extras",
    expenses: "Expenses",
    subInvoicing: "Received Invoices",
    clientInvoicing: "Client Invoicing",
    dailyLog: "Daily Log",
    portfolioProfitability: "Portfolio",
    projectView: "Dashboard",
    brain: "Brain",
    onx: "OnX",
    schedule: "Schedule",
    documents: "Documents",
    rfi: "RFI",
    userFallback: "User"
  },
  bulk: {
    selectedOne: "1 selected",
    selected: "{count} selected",
    clear: "Clear",
    clearTooltip: "Clear selection",
    cancel: "Cancel",
    export: "Export",
    exportCsv: "Export CSV",
    exportPdf: "Export PDF",
    changeStatus: "Change status",
    submit: "Submit",
    approve: "Approve",
    reject: "Reject",
    draft: "Draft",
    delete: "Delete",
    archive: "Archive",
    downloadPdf: "Download PDF",
    downloading: "Downloading…",
    scan: "Scan",
    scanning: "Scanning {count}…",
    createContract: "Create contract"
  },
  common: {
    newProject: "New Project",
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    close: "Close",
    loading: "Loading...",
    saving: "Saving...",
    creating: "Creating...",
    search: "Search",
    reset: "Reset",
    clear: "Clear",
    actions: "Actions",
    status: "Status",
    noData: "No data found",
    na: "N/A",
    searchPlaceholder: "Search...",
    searchOptions: "Search options...",
    searchProjects: "Search projects...",
    searchSubcontract: "Search subcontract...",
    selectPlaceholder: "Select...",
    selectAnOption: "Select an option",
    none: "None",
    all: "All",
    unknownSubcontractor: "Unknown subcontractor",
    noResults: "No results found",
    noOptions: "No options available",
    accessDenied: {
      banner: "You do not have access to that page. Contact your administrator.",
      title: "No access",
      message: "Your profile grants access to no page. Contact your administrator so they can assign you a permission template.",
      backToList: "Back to the project list"
    }
  },
  userManagement: {
    pageTitle: "User Management",
    pageSubtitle: "Manage roles, OnSite+ access, and account status for your organization.",
    header: {
      myProfile: "My Profile",
      manageUsers: "Manage users",
      logout: "Logout"
    },
    table: {
      user: "User",
      role: "Function",
      roleHelp: "The function is used to assign the person to projects (project manager, superintendent, coordinator, assistant).",
      trade: "Trade",
      onsitePlus: "OnSite+",
      viewAllProjects: "All projects",
      status: "Status",
      actions: "Actions"
    },
    roles: {
      admin: "Admin",
      pm: "Project Manager",
      assistant: "Assistant",
      supervisor: "Supervisor",
      coordinator: "Coordinator",
      accountant: "Accountant",
      worker: "Worker"
    },
    status: {
      active: "Active",
      inactive: "Deactivated"
    },
    actions: {
      deactivate: "Deactivate account",
      reactivate: "Reactivate account"
    },
    tooltips: {
      cannotEditOwnRole: "You cannot change your own role",
      cannotDeactivateSelf: "You cannot deactivate your own account",
      viewAllProjectsAdmin: "Admins already see all projects",
      viewAllProjectsAccountant: "Accountants already see all projects",
      viewAllProjectsSelf: "You cannot modify your own access"
    },
    modals: {
      roleChange: {
        title: "Confirm role change",
        message: "Change {name} from {oldRole} to {newRole}?"
      },
      onsitePlus: {
        title: "OnSite+ Access",
        enableMessage: "Enable OnSite+ access for {name}?",
        disableMessage: "Disable OnSite+ access for {name}?"
      },
      deactivate: {
        title: "Deactivate account",
        message: "Deactivate the account of {name}?",
        warning: "This person will no longer be able to log in or see any projects."
      },
      reactivate: {
        title: "Reactivate account",
        message: "Reactivate the account of {name}?"
      },
      blocked: {
        lastAdminUsersPage: "Not possible: this person is the last active admin, and this template does not open the Users page. They would lose access management with no way back.",
        title: "Action blocked",
        message: "The organization must always have at least one active administrator. This action cannot be completed."
      },
      viewAllProjects: {
        title: "Modify Extended Access",
        enableMessage: "Grant {name} read access to all projects and invoices?",
        disableMessage: "Revoke extended access for {name}?"
      }
    },
    toasts: {
      roleUpdated: "Role updated successfully.",
      onsiteUpdated: "OnSite+ access updated.",
      viewAllProjectsUpdated: "Extended access updated.",
      deactivated: "Account deactivated.",
      reactivated: "Account reactivated.",
      invitedEmail: "Invitation sent. The user will receive an email to set their password.",
      invitedPassword: "User created. Remember to communicate the temporary password to them directly."
    },
    errors: {
      loadFailed: "Failed to load users. Please try again.",
      updateFailed: "Update failed. Please try again.",
      inviteSeatLimit: "Seat limit reached. You cannot add more users to this organization.",
      inviteEmailExists: "This email address is already associated with an account.",
      inviteNotAdmin: "You must be an active administrator to invite users.",
      inviteFailed: "Invitation failed. Please try again.",
      inviteEmailRequired: "Email address is required.",
      invitePasswordTooShort: "The temporary password must be at least 12 characters."
    },
    invite: {
      button: "Invite a user",
      title: "Invite a user",
      subtitle: "Add a new member to your organization.",
      emailLabel: "Email address",
      emailPlaceholder: "name@company.com",
      fullNameLabel: "Full name (optional)",
      fullNamePlaceholder: "Jean Tremblay",
      roleLabel: "Role",
      tradeLabel: "Trade",
      tradePlaceholder: "No trade",
      productAccessLabel: "Product access",
      productOnsitePM: "OnsitePM",
      productOnsitePlus: "OnSite+",
      productOnsiteES: "OnsiteES",
      productComingSoon: "coming soon",
      modeLabel: "Activation method",
      modeEmail: "Send an invitation email",
      modePassword: "Set a temporary password",
      tempPasswordLabel: "Temporary password",
      generate: "Generate",
      copy: "Copy",
      copied: "Copied!",
      submit: "Create user",
      submitting: "Creating...",
      cancel: "Cancel"
    }
  },
  settings: {
    organization: {
      title: "Organization Info",
      addressLabel: "Address",
      phoneLabel: "Phone",
      saveBtn: "Save",
      saveSuccess: "Info updated",
      taglineLabel: "Slogan / Tagline",
      emailLabel: "Email",
      websiteLabel: "Website",
      rbqLabel: "RBQ",
      tpsLabel: "TPS Number",
      tvqLabel: "TVQ Number",
      logoLabel: "Company logo",
      logoUploadBtn: "Choose file",
      logoRemoveBtn: "Remove",
      logoSvgNotSupported: "SVG logos are not supported (PNG, JPG, WEBP only)",
      logoTooLarge: "Logo too large (max 5 MB)",
      logoUploadSuccess: "Logo updated successfully",
      logoRemoveSuccess: "Logo removed"
    }
  },
  status: {
    active: "Active",
    completed: "Completed",
    pending: "Pending",
    onHold: "On Hold",
    archived: "Archived",
    draft: "Draft",
    submitted: "Submitted",
    to_resubmit: "To resubmit",
    approved: "Approved",
    rejected: "Rejected",
    pendingSignature: "Pending Signature",
    signed: "Signed",
    cancelled: "Cancelled"
  },
  login: {
    title: "OnSitePm",
    subtitle: "Sign in to your account",
    emailLabel: "Email",
    emailPlaceholder: "Enter your email",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    signingIn: "Signing in...",
    signIn: "Sign In",
    demoAccount: "Demo Account",
    useDemoCredentials: "Use Demo Credentials",
    emailField: "Email:",
    passwordField: "Password:",
    needHelp: "Need help? Contact support",
    loadingAuth: "Loading...",
    signInTitle: "Sign in to your workspace",
    signInSubtitle: "Enter your credentials to access your projects",
    signInButton: "Sign In",
    noAccount: "Don\'t have an account?",
    signUp: "Sign Up",
    demoAccess: "Demo Access",
    useDemo: "Use Demo Credentials",
    poweredBy: "OnsitePM",
    contracts: "Contracts",
    subcontractors: "Subcontractors",
    financials: "Financials",
    heroTagline: "Protect your profit before the project ends",
    heroSubtitle: "Operational intelligence for modern construction teams.",
    tagline: "Operational intelligence for modern construction teams",
    accountInactive: "Your account has been deactivated.",
    accountInactiveHint: "Please contact your administrator to reactivate your access."
  },
  profile: {
    title: "My Profile",
    subtitle: "Manage your personal information",
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    emailLabel: "Email",
    emailPlaceholder: "Enter your email",
    phoneLabel: "Phone",
    phonePlaceholder: "Enter your phone number",
    roleLabel: "Role",
    roleReadOnly: "Role is read-only",
    roleNotSet: "Not set",
    companyLabel: "Company",
    companyPlaceholder: "Enter your company name",
    saveChanges: "Save Changes",
    saving: "Saving...",
    saveSuccess: "Profile updated successfully",
    loadingProfile: "Loading profile...",
    avatar: {
      editPhoto: "Edit photo",
      removePhoto: "Remove photo",
      invalidType: "Unsupported format (JPG, PNG, WebP only)",
      tooLarge: "Image too large (max 2 MB)",
      uploadFailed: "Upload failed",
      uploadSuccess: "Photo updated"
    }
  },
  dashboard: {
    title: "Project Portfolio",
    subtitle: "Global dashboard of all projects",
    kpi: {
      totalProjects: "Total Projects",
      totalContractValue: "Total Contract Value",
      totalSubRevised: "Total Sub Revised",
      totalLiveProfit: "Total Live Profit",
      averageMargin: "Average Margin %"
    },
    charts: {
      revenueByProject: "Revenue by Project",
      statusBreakdown: "Project Status Breakdown",
      profitTrend: "Profit Trend"
    },
    table: {
      projectNumber: "Project #",
      projectName: "Project Name",
      client: "Client",
      city: "City",
      created: "Created",
      submission: "Submission",
      actions: "Actions"
    },
    filters: {
      allStatuses: "All Statuses",
      allTime: "All Time",
      today: "Today",
      thisWeek: "This Week",
      thisMonth: "This Month",
      thisQuarter: "This Quarter",
      thisYear: "This Year",
      marginRange: "Margin Range: {min}% - {max}%",
      statusLabel: "Status",
      dateRangeLabel: "Date Range"
    },
    empty: "No projects found",
    bulk: {
      selected: "{count} projects selected",
      selectedOne: "1 project selected",
      export: "Export",
      archive: "Archive",
      updateStatus: "Update Status"
    }
  },
  projectList: {
    title: "Projects",
    subtitle: "Manage and track all active projects",
    searchPlaceholder: "Search by name...",
    newProject: "New Project",
    noProjectsAssigned: {
      title: "No projects assigned to you yet",
      message: "Contact your administrator to be assigned to a project."
    },
    table: {
      name: "Project Name",
      contract: "Contract Value",
      contractValueRevised: "Contract Value (revised)",
      expenses: "Expenses",
      profit: "Profit",
      margin: "Margin",
      status: "Status",
      actions: "Actions"
    },
    filter: {
      title: "Filter Projects",
      clear: "Clear Filters",
      searchName: "Search by name...",
      searchNumber: "Search by number...",
      searchUniversal: "Search by name, number or client...",
      allClients: "All Clients",
      projectsCount: "Projects",
      yearFilter: {
        all: "All"
      },
      empty: "No projects found",
      projectNumber: "Project #",
      edit: "Edit",
      archive: "Archive",
      delete: "Delete"
    }
  },
  newProjectModal: {
    titleCreate: "New Project",
    titleEdit: "Edit Project",
    subtitleCreate: "Create and configure a new project",
    subtitleEdit: "Update project details and configuration",
    sectionIdentity: "Project Identity",
    sectionClientFinancials: "Client & Financials",
    sectionClassification: "Classification",
    sectionSchedule: "Schedule",
    sectionLocation: "Location",
    sectionNotes: "Notes",
    projectName: "Project Name",
    projectNamePlaceholder: "Enter project name",
    projectNameRequired: "Project name is required",
    projectNumber: "Project Number",
    projectNumberPlaceholder: "Auto-generated if left empty",
    projectNumberHelper: "Leave empty for automatic numbering (e.g., P26-010)",
    projectNumberDuplicate: "This project number already exists in your organization",
    clientProjectNumber: "Client Project Number",
    clientProjectNumberPlaceholder: "Optional — e.g., architect's reference",
    clientProjectNumberHelper: "External reference for client or architect (optional)",
    clientNumberLabel: "Client #",
    contractValue: "Contract Value (Auto-calculated from budgets)",
    contractValuePlaceholder: "Enter contract value",
    contractValueAuto: "Calculated from project costs",
    contractValueHelper: "Auto-calculated from budget cost codes",
    client: "Client",
    clientPlaceholder: "Enter client name",
    constructionType: "Construction Type",
    constructionTypePlaceholder: "Select construction type",
    workNature: "Work Nature",
    workNaturePlaceholder: "Select work nature",
    architect: "Architect",
    architectPlaceholder: "Enter architect name",
    submissionDate: "Submission Date",
    startDate: "Start Date",
    endDate: "End Date",
    timeframe: "Project Timeframe",
    timeframeHelper: "Auto-calculated from start and end dates",
    address: "Address",
    addressPlaceholder: "Enter address",
    city: "City",
    cityPlaceholder: "Enter city",
    state: "State",
    statePlaceholder: "Enter state",
    zipCode: "Zip Code",
    zipCodePlaceholder: "Enter zip code",
    description: "Description",
    descriptionPlaceholder: "Enter project description",
    statusLabel: "Status",
    cancel: "Cancel",
    createProject: "Create Project",
    saveChanges: "Save Changes",
    creating: "Creating...",
    saving: "Saving..."
  },
  projectStatus: {
    active: "In progress",
    "on-hold": "On hold",
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    archived: "Archived",
    deleted: "Deleted"
  },
  deleteModal: {
    title: "Delete Project",
    message: "This will remove the project from your account. This action can be reversed later.",
    projectLabel: "Project:",
    cancel: "Cancel",
    delete: "Delete"
  },
  projectDirectory: {
    title: "Project Directory",
    subtitle: "Team and contacts assigned to this project",
    sidebarLabel: "Directory",
    tabs: {
      subcontractors: "Subcontractors",
      clients: "Clients",
      professionals: "Professionals"
    },
    searchPlaceholder: "Search by company, contact, email...",
    filterAllTrades: "All Trades",
    filterAllProfessions: "All Professions",
    inactive: "Inactive",
    columns: {
      companyName: "Company Name",
      trade: "Trade",
      profession: "Profession",
      role: "Role (this project)",
      contactPerson: "Contact Person",
      phone: "Phone",
      email: "Email",
      address: "Address",
      rating: "Rating",
      actions: "Actions"
    },
    buttons: {
      add: "Add",
      edit: "Edit",
      detach: "Detach",
      cancel: "Cancel",
      save: "Save"
    },
    modals: {
      attachSubcontractor: { title: "Attach Subcontractor to Project" },
      attachClient: { title: "Attach Client to Project" },
      attachProfessional: { title: "Attach Professional to Project" },
      editTitle: "Edit Project Entry",
      attachField: {
        label: "Select from company directory",
        placeholder: "Search and select..."
      },
      role: {
        label: "Role on this project",
        placeholderSub: "e.g. Foreman, Lead Carpenter",
        placeholderClient: "e.g. Primary Contact, Project Sponsor",
        placeholderProfessional: "e.g. Project Architect, Mechanical Engineer of Record"
      },
      notes: {
        label: "Notes (optional)",
        placeholder: "Internal notes about this team member on this project..."
      },
      activeLabel: "Active on this project",
      detachConfirm: {
        title: "Remove from Project",
        message: "Remove {name} from this project? They will remain in the company directory."
      },
      noAvailable: "All entries already attached to this project",
      errorSelectEntry: "Please select an entry from the directory",
      errorRoleRequired: "Role is required",
      errorDuplicate: "This entry is already attached to this project",
      errorGeneric: "An error occurred. Please try again."
    },
    toast: {
      attached: "Entry attached to project",
      updated: "Entry updated",
      detached: "Entry removed from project"
    },
    empty: {
      subcontractors: "No subcontractors attached to this project yet",
      clients: "No clients attached to this project yet",
      professionals: "No professionals attached to this project yet"
    },
    permissions: {
      readOnly: "View only — only the PM or admin can edit this directory"
    },
    errors: {
      noProjectId: "Project not found"
    }
  },
  directory: {
    title: "Directory",
    subtitle: "Manage company contacts (subcontractors, clients, professionals)",
    addSubcontractor: "Add Subcontractor",
    addClient: "Add Client",
    addProfessional: "Add Professional",
    types: {
      subcontractor: "Subcontractors",
      client: "Clients",
      professional: "Professionals"
    },
    contactsCount: "{count} Contacts",
    filter: {
      title: "Filter Directory",
      contacts: "Contacts",
      clearFilters: "Clear Filters",
      searchPlaceholder: "Search by company, contact, email...",
      allTrades: "All Trades",
      allProfessions: "All Professions"
    },
    table: {
      companyName: "Company Name",
      trade: "Trade",
      contactPerson: "Contact Person",
      phone: "Phone",
      email: "Email",
      actions: "Actions",
      profession: "Profession",
      rating: "Rating",
      address: "Address",
      notes: "Notes",
      compliance: "Compliance"
    },
    empty: "No contacts found",
    modal: {
      titleAdd: "Add New Subcontractor",
      titleEdit: "Edit Subcontractor",
      titleAddClient: "Add Client",
      titleEditClient: "Edit Client",
      titleAddProfessional: "Add Professional",
      titleEditProfessional: "Edit Professional",
      companyName: "Company Name",
      companyNamePlaceholder: "Enter company name",
      trade: "Trade",
      tradeSelectPlaceholder: "Select trade",
      contactPerson: "Contact Person",
      contactPersonPlaceholder: "Enter contact person name",
      phone: "Phone",
      phonePlaceholder: "(555) 123-4567",
      email: "Email",
      emailPlaceholder: "contact@company.com",
      profession: "Profession",
      professionPlaceholder: "Select a profession",
      address: "Address",
      addressPlaceholder: "Enter address",
      notes: "Notes",
      notesPlaceholder: "Internal notes (visible to your team only)",
      rating: "Rating",
      cancel: "Cancel",
      add: "Add",
      update: "Update",
      close: "Close",
      edit: "Edit",
      save: "Save",
      status: "Status",
      statusActive: "Active",
      statusInactive: "Inactive",
      sectionSpecialties: "Specialties",
      specialtyPrimary: "Primary specialty",
      specialtyOthers: "Other specialties",
      specialtyUndefined: "To be defined",
      specialtiesLoading: "Loading specialties…",
      specialtiesLoadError: "Specialties could not be loaded.",
      specialtiesEditHint: "Specialties are edited in OnSiteBid.",
      tradeLabel: "Trade",
      sectionEstimation: "Estimating contact",
      estimationHint: "Receives bid requests. Empty = contact person.",
      sectionCompany: "Company",
      cityOrAddress: "City or address",
      taxId: "NEQ",
      sectionDocuments: "Documents",
      invalidEmail: "Invalid email address.",
      saving: "Saving...",
      requiredFields: "Please fill in all required fields",
      sectionGeneral: "General information",
      sectionContact: "Primary contact",
      sectionCompliance: "Compliance",
      sectionContacts: "Additional contacts",
      sectionRating: "Rating",
      rbqNumber: "RBQ number",
      rbqExpiry: "RBQ expiry",
      insuranceExpiry: "Insurance expiry",
      insuranceDoc: "Insurance document",
      rbqDoc: "RBQ document",
      addContact: "+ Add contact",
      contactRole: "Role",
      contactName: "Name",
      contactEmail: "Email",
      contactPhone: "Phone",
      chooseFile: "Choose a file",
      noAdditionalContacts: "No additional contacts",
      viewFile: "View",
      removeFile: "Remove",
      svgNotSupported: "SVG not supported (PDF, JPG, PNG only)",
      fileTooLarge: "File too large (max 20 MB)"
    },
    deleteModal: {
      title: "Delete Contact",
      message: "Are you sure you want to delete this contact? This action cannot be undone.",
      cancel: "Cancel",
      delete: "Delete"
    },
    dropdown: {
      edit: "Edit",
      delete: "Delete"
    },
    expiry: {
      expired: "Expired",
      expiresIn: "Expires in {days}d",
      upToDate: "Up to date"
    }
  },
  contactRoles: {
    accounting: "Accounting",
    projectManagement: "Project management",
    projectLead: "Project lead",
    coordinator: "Coordinator",
    other: "Other"
  },
  budget: {
    errors: {
      divisionNeedsDigit: "The division number must contain at least one digit (e.g. \"2\" or \"02\")."
    },
    title: "Financial Overview",
    subtitle: "Track project costs, performance and profitability in real time",
    kpi: {
      initialBudget: "Initial Budget",
      extraCredit: "Extra/Credit",
      totalActualCost: "Total Actual Cost",
      liveProfit: "Live Profit",
      profitMargin: "Profit Margin"
    },
    tabs: {
      construction: "Construction Cost",
      general: "General Cost",
      admin: "Admin & Profit",
      extras: "Extras & Credits"
    },
    addCostCode: "Add Cost Code",
    addFirstCostCode: "Add First Cost Code",
    table: {
      costCode: "Cost Code",
      description: "Description",
      budget: "Budget",
      actual: "Actual",
      commitment: "Commitment",
      initialValue: "Initial Value",
      expenses: "Expenses",
      forecast: "Forecast",
      pl: "P/L",
      plPercent: "P/L %",
      actions: "Actions",
      totalExpenses: "total expenses",
      type: "Type",
      coNumber: "CO #"
    },
    filters: {
      all: "All Cost Codes",
      overBudget: "Over Budget Only",
      underBudget: "Under Budget Only",
      zeroVariance: "Zero Variance",
      highVariance: "High Variance (>10%)"
    },
    empty: {
      all: "No cost codes in this category",
      filtered: "No cost codes match the selected filter",
      emptyTitle: "No cost codes yet",
      emptyDescription: 'Click "Add Division" to get started'
    },
    clickToEdit: "Click to edit",
    tooltips: {
      deleteCostCode: "Delete cost code",
      addCostCodeToDivision: "Add cost code to this division",
      divisionActions: "Division actions",
      clearFilter: "Clear filter",
      collapse: "Collapse",
      expand: "Expand",
      collapseAll: "Collapse all",
      expandAll: "Expand all",
      commitmentTotal: "Total"
    },
    extras: {
      loadingLines: "Loading lines...",
      noLines: "No lines found"
    },
    division: {
      label: "Division",
      subtotal: "Subtotal",
      items: "items",
      ungrouped: "No Division",
      editDivision: "Edit Division",
      deleteDivision: "Delete Division",
      cannotDeleteDivision: "Cannot delete division because it contains cost codes.",
      cannotDeleteWithCount: "This division contains {count} cost code(s). Delete them first.",
      deleteConfirmMessage: "Are you sure you want to delete this division?",
      deleteIrreversible: "This action cannot be undone.",
      deleting: "Deleting...",
      errorUpdating: "Error updating division"
    },
    addDivision: {
      title: "Add Division",
      divisionNumber: "Division Number",
      divisionNumberPlaceholder: "Ex: 02",
      divisionTitle: "Division Title",
      divisionTitlePlaceholder: "Ex: Existing Conditions",
      submit: "Add Division",
      searchPlaceholder: "Search division...",
      addToProject: "Add to Project",
      alreadyAdded: "(added)",
      divisionLabel: "Division {number}",
      noDivisionsFound: "No divisions found",
      adding: "Adding...",
      toastAlreadyAdded: "Division already added",
      errorNoDigit: "Division number must contain at least one digit",
      errorAlreadyExists: "Division {code} already exists",
      errorCreating: "Error creating division",
      errorAddFailed: "Failed to add division"
    },
    addCostCodeModal: {
      title: "Add Cost Code",
      division: "Division",
      costCode: "Cost Code",
      description: "Description",
      descriptionPlaceholder: "e.g. Selective demolition",
      submit: "Add Cost Code"
    },
    deleteCostCode: {
      confirmTitle: "Delete Cost Code",
      confirmMessage: "Are you sure you want to delete this cost code?",
      blockedTitle: "Cannot Delete Cost Code",
      blockedMessage: "This cost code cannot be deleted because it is linked to financial records.",
      linkedContracts: "Linked Contracts",
      invoiceLabel: "Invoice",
      linkedInvoices: "Linked Invoices",
      linkedExpenses: "Linked Expenses",
      warningTitle: "Warning",
      warningMessage: "This cost code has an Actual value greater than $0. Deleting it may cause inconsistencies.",
      deleteAnyway: "Delete Anyway"
    },
    createContract: {
      button: "Create Contract",
      cancel: "Cancel",
      countSelected: "{count} selected",
      success: "{count} contract placeholder(s) created",
      error: "Failed to create contracts. Please try again."
    }
  },
  contracts: {
    title: "Contracts Management",

    subtitle: "Manage subcontractor contracts",
    addContract: "Add Contract",
    createSubcontractAgreement: "Create a subcontract agreement",
    documentType: "Document Type",
    pricingModel: "Pricing Model",
    documentTypeOptions: {
      contract: "Contract",
      purchaseOrder: "Purchase Order"
    },
    pricingModelOptions: {
      fixed: "Fixed",
      laborMaterial: "Labor & Material",
      materialSupply: "Material Supply"
    },
    costCode: "Cost Code",
    subcontractor: "Subcontractor",
    contractValue: "Contract Value",
    notes: "Notes",
    cancel: "Cancel",
    addContractButton: "Add Contract",
    kpi: {
      totalContracts: "Total Contracts",
      totalValue: "Total Value",
      pendingSignature: "Pending Signature",
      signed: "Signed"
    },
    filters: {
      searchPlaceholder: "Search contracts...",
      subcontractorPlaceholder: "Subcontractor",
      tradePlaceholder: "Trade",
      allStatus: "All Status",
      pendingSignature: "Pending Signature",
      signed: "Signed",
      clear: "Clear",
      contracts: "contracts"
    },
    table: {
      title: "Contract List",
      addContract: "Add Contract",
      noContracts: "No contracts found. Click \"Add Contract\" to create one.",
      coAv: "CO",
      revisedContract: "Revised Contract",
      status: "Status",
      actions: "Actions"
    },
    empty: {
      emptyTitle: "No contracts yet",
      emptyDescription: 'Click "Add Contract" to get started'
    },
    noteModal: {
      title: "Contract Note",
      placeholder: "Add a note for this contract...",
      addNote: "Add note",
      editNote: "Edit note"
    },
    actions: {
      edit: "Edit",
      viewDocuments: "View Documents",
      delete: "Delete"
    },
    addModal: {
      title: "Add Contract",
      editTitle: "Edit Contract",
      editSubtitle: "Update the contract details below",
      costCodeLabel: "Cost Code *",
      costCodePlaceholder: "Select a cost code",
      noCostCodes: "No construction cost codes available for this project.",
      subcontractorLabel: "Subcontractor *",
      subcontractorPlaceholder: "Select a subcontractor",
      noSubcontractors: "No subcontractors available. Please add subcontractors first.",
      contractValueLabel: "Contract Value *",
      contractValuePlaceholder: "0.00",
      notesLabel: "Notes (Optional)",
      notesPlaceholder: "Add any additional notes...",
      cancel: "Cancel",
      addContract: "Add Contract",
      saveChanges: "Save Changes",
      adding: "Adding...",
      saving: "Saving...",
      validationCostCode: "Please select a cost code",
      validationSubcontractor: "Please select a subcontractor",
      validationContractValue: "Contract value must be greater than 0",
      failedCreate: "Failed to create contract. Please try again.",
      failedUpdate: "Failed to update contract. Please try again."
    },
    statusLocked: {
      tooltip: "Assign a subcontractor to change the status"
    },
    statusOptions: {
      toAssign: "To Assign",
      draft: "Draft",
      pendingSignature: "Pending signature",
      sent: "Sent",
      signed: "Signed",
      completed: "Completed",
      cancelled: "Cancelled"
    }
  },
  extras: {
    createCOModal: {
      title: "Create Subcontract Change Order",
      submit: "Create Change Order"
    },

    title: "Directives & Extras",
    subtitle: "Manage extra work orders and credits",
    kpi: {
      draft: "Draft",
      submitted: "Submitted",
      approved: "Approved",
      extra: "Extra",
      credit: "Credit",
      total: "Total",
      rejected: "Rejected"
    },
    toolbar: {
      items: "item",
      itemsPlural: "items",
      approved: "approved",
      addExtraCredit: "Add Extra/Credit",
      allStatuses: "All statuses",
      searchPlaceholder: "Search extras and credits..."
    },
    table: {
      number: "#",
      type: "Type",
      coNumber: "CO #",
      co: "Directive #",
      title: "Title",
      receivedDate: "Received Date",
      amount: "Submitted Cost",
      costAccepted: "Cost Accepted",
      status: "Status",
      created: "Created",
      actions: "Actions",
      description: "Description",
      submitted: "Submitted",
      accepted: "Accepted",
      selectAll: "Select all",
      changeOrder: "CO"
    },
    notes: {
      title: "Internal notes",
      none: "No notes",
      empty: "No notes yet.",
      placeholder: "Add a note… (Ctrl+Enter to save)",
      add: "Add",
      save: "Save",
      edit: "Edit",
      delete: "Delete",
      deleteConfirm: "Delete this note?",
      edited: "edited",
      visibility: "Visible to the project team only · never in the PDF",
      loadError: "Could not load the notes.",
      saveError: "Could not save the note.",
      tooLong: "The note exceeds {max} characters.",
      notOwn: "You can only edit your own notes."
    },
    co: {
      menuLabel: "Client's change order (CO)",
      pending: "Pending",
      received: "Received",
      receivedOn: "CO received on {date}",
      markReceived: "CO received…",
      resetPending: "Set back to pending",
      resetTitle: "Set the CO back to pending",
      resetMessage: "The change order for “{title}” will go back to “Pending”. The document stays in the attachments.",
      docRequired: "upload the CO: it becomes “Received” once the upload succeeds",
      receivedToast: "CO received — document added to attachments",
      resetToast: "CO set back to pending",
      error: "Could not save the change order",
      filterPending: "CO: pending",
      filterReceived: "CO: received",
      notRequired: "Not required",
      internalDefault: "Internal"
    },
    types: {
      extra: "Extra",
      credit: "Credit"
    },
    addExtra: "Add Extra",
    addCredit: "Add Credit",
    editExtra: "Edit Extra",
    editCredit: "Edit Credit",
    viewExtra: "Extra details",
    viewCredit: "Credit details",
    viewLockedApproved: "An approved extra can no longer be edited",
    empty: {
      noExtras: "No extras or credits yet",
      clickToAdd: "Click \"Add Extra\" to get started"
    },
    deleteDialog: {
      title: "Delete Entry",
      message: "Are you sure you want to delete this extra / credit? This action cannot be undone.",
      cancel: "Cancel",
      delete: "Delete"
    },
    locked: "Locked — a signed change order exists",
    rowActions: {
      actions: "Actions",
      documents: "Documents",
      noDocuments: "No documents",
      moreDocuments: "+{count} more",
      revertStatus: "Revert status",
      changeStatus: "Change status"
    },
    bulk: {
      selected: "{count} selected",
      approve: "Approve",
      approveTooltip: "Approve selected",
      submit: "Submit",
      submitTooltip: "Mark as submitted",
      status: "Status",
      draft: "Draft",
      reject: "Reject",
      export: "Export",
      exportCsv: "Export CSV",
      exportPdf: "Export PDF",
      clear: "Clear",
      clearTooltip: "Clear selection"
    },
    export: {
      title: "Extras & Credits",
      selectedLabel: "Selected ({count})",
      exportedOn: "Exported {date}",
      totalItems: "Total ({count} items)",
      csvTooltip: "Export all as CSV",
      pdfTooltip: "Export all as PDF",
      columns: {
        type: "Type",
        coNumber: "C.O. #",
        description: "Description",
        submitted: "Submitted",
        accepted: "Accepted",
        status: "Status"
      }
    },
    downloadPdf: "Download PDF",
    approve: {
      title: "Create change orders",
      subtitle: "Select the lines to turn into change orders for each subcontractor.",
      notes: "Notes",
      description: "Description",
      descriptionRequired: "Describe what the change order must contain",
      confirm: "Create change orders",
      cancel: "Cancel",
      total: "Total"
    },
    addModal: {
      title: "Add Extra / Credit",
      typeLabel: "Type",
      typeExtra: "+ Extra",
      credit: "− Credit",
      titleLabel: "Title",
      titlePlaceholder: "e.g. Additional Electrical Work",
      titleRequired: "Title is required",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Optional description...",
      workDescriptionLabel: "Work description",
      optional: "(optional)",
      coNumberFromDirective: "Number of the linked directive (read-only)",
      amountLabel: "Amount",
      amountPlaceholder: "0.00",
      amountRequired: "Amount must be greater than 0",
      divisionLabel: "Division",
      divisionPlaceholder: "Select a division...",
      coNumberLabel: "Directive number",
      coNumberPlaceholder: "e.g. CO-001",
      receivedDateLabel: "Received Date",
      costSubmittedLabel: "Cost Submitted",
      costAcceptedLabel: "Cost Accepted",
      subtitle: "Create extra work or credit",
      subtitleCredit: "Create a credit against this project",
      linesRequired: "Add at least one line",
      loadingFiles: "Loading files...",
      filesAfterSave: "Files will be uploaded after saving.",
      extraLines: "Subcontractor lines",
      creditLines: "Credit lines",
      linesTotalLabel: "Lines total (pre-tax)",
      addLine: "Add a line",
      cancel: "Cancel",
      add: "Add",
      saving: "Saving..."
    },
    editModal: {
      title: "Edit Extra / Credit",
      typeLabel: "Type",
      extraWork: "+ Extra Work",
      credit: "− Credit",
      titleLabel: "Title",
      titlePlaceholder: "e.g. Additional Electrical Work",
      titleRequired: "Title is required",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Optional description...",
      amountLabel: "Amount",
      amountPlaceholder: "0.00",
      amountRequired: "Amount must be greater than 0",
      statusLabel: "Status",
      cancel: "Cancel",
      saveChanges: "Save Changes",
      saving: "Saving..."
    },
    category: {
      label: "Category",
      required: "Please select a category",
      contractual: "Contractual",
      backcharge: "Backcharge",
      internal_error: "Internal Error",
      absorbed_tm: "Absorbed T&M",
      dispute: "Dispute"
    },
    divisions: {
      architecture: "Architecture",
      electrical: "Electrical",
      plumbing: "Plumbing",
      hvac: "HVAC",
      mechanical: "Mechanical",
      structural: "Structural",
      civil: "Civil"
    },
    tabs: {
      directives: "Directives",
      extras: "Extras & Credits"
    },
    directives: {
      toolbar: {
        searchPlaceholder: "Search directives...",
        newDirective: "New directive"
      },
      table: {
        number: "No.",
        nomenclature: "Nomenclature",
        division: "Division",
        title: "Title & description",
        receivedOn: "Received on",
        status: "Status",
        documents: "Files",
        documentsCountUnknown: "File count unavailable — click to open",
        noDocuments: "No document attached",
        overdue: "To assess for {days} d",
        overdueShort: "{days} d",
        actions: "Actions"
      },
      outcome: {
        pending: "To assess",
        extra: "Extra created",
        credit: "Credit created",
        no_impact: "No impact"
      },
      actions: {
        createExtra: "Create an extra",
        createCredit: "Create a credit",
        menu: "Actions",
        edit: "Edit",
        history: "History",
        delete: "Delete"
      },
      empty: {
        none: "No directives yet",
        createFirst: "Click \"New directive\" to get started"
      },
      documentsSubtitle: "Directive no. {number}",
      files: {
        hint: "PDF only · Max 20 MB",
        open: "Open",
        download: "Download",
        rename: "Rename",
        renameSave: "Save name",
        renameCancel: "Cancel",
        standardName: "Standard name",
        errorPdfOnly: "{name}: PDF only",
        errorTooLarge: "{name}: exceeds 20 MB",
        errorUpload: "{name}: upload failed",
        errorLoad: "The files could not be loaded.",
        errorEmptyName: "The name cannot be empty.",
        errorRename: "The name could not be saved.",
        errorDelete: "The file could not be deleted."
      },
      create: {
        title: "New directive",
        subtitle: "Record a directive received from the professional",
        divisionLabel: "Division",
        divisionPlaceholder: "Select a division...",
        divisionRequired: "Division is required",
        professionalLabel: "Professional",
        professionalPlaceholder: "Select a professional...",
        professionalRequired: "Professional is required",
        noProfessionals: "No active professional on this project",
        titleLabel: "Title",
        titlePlaceholder: "e.g. Rework of the lobby ceiling",
        titleRequired: "Title is required",
        nomenclatureLabel: "Nomenclature",
        nomenclaturePlaceholder: "e.g. DDC-A-01",
        nomenclatureRequired: "Nomenclature is required",
        nomenclatureHint: "Format DDC-discipline-number, e.g. DDC-A-01.",
        nomenclatureSuggest: "Suggested format: DDC-A-01 (you can keep what you typed).",
        nomenclatureTaken: "This nomenclature already exists in this project.",
        dateLabel: "Directive date",
        dateHint: "The date printed on the received document.",
        dateRequired: "Directive date is required",
        rfiLabel: "Comes from an RFI?",
        rfiPlaceholder: "None",
        descriptionLabel: "Description",
        descriptionPlaceholder: "What the directive asks for...",
        descriptionRequired: "Description is required",
        documentsLabel: "Attachments",
        documentsHint: "PDF only · Max 20 MB",
        submit: "Create directive",
        saving: "Saving...",
        numberCollision: "The assigned number is already taken. Please try again.",
        error: "The directive could not be created."
      },
      outcomeError: "The directive outcome could not be saved.",
      extraCreatedToast: "Extra created — see the Extras & Credits tab",
      creditCreatedToast: "Credit created — see the Extras & Credits tab",
      noImpactToast: "Directive filed as no impact",
      rollbackNotice: "The extra was removed: the directive could not be updated.",
      orphanNotice: "Extra {number} created, but the directive could not be updated. Link them manually.",
      none: "No directive",
      edit: {
        mode: "Editing",
        submit: "Save",
        error: "The directive could not be saved.",
        discardTitle: "Discard changes?",
        discardMessage: "Unsaved changes will be lost.",
        discardConfirm: "Discard",
        keepEditing: "Keep editing"
      },
      outcomeChange: {
        title: "Change outcome",
        confirm: "Change outcome",
        toLabel: "The directive will be set to \"{label}\".",
        detach: "Extra {number} will be detached from this directive, but kept.",
        linkedExtra: "linked",
        create: "A draft {type} will be created and linked to this directive.",
        typeExtra: "extra",
        typeCredit: "credit"
      },
      delete: {
        title: "Delete directive",
        message: "Directive {name} and its attachments will be permanently deleted.",
        confirm: "Delete",
        linkedExtra: "Detach the linked extra first: change the outcome to \"To assess\" or \"No impact\".",
        error: "The directive could not be deleted."
      },
      history: {
        title: "History",
        empty: "No changes recorded.",
        error: "The history could not be loaded.",
        created: "created",
        system: "System",
        unknownRef: "(not found)",
        note: "Deletions are not logged.",
        fields: {
          outcome: "Outcome",
          nomenclature: "Nomenclature",
          title: "Title",
          division: "Division",
          date_directive: "Received on",
          professional_id: "Professional",
          rfi_id: "Source RFI",
          extra_id: "Linked extra / credit"
        }
      },
      detail: {
        eyebrow: "Directive",
        open: "Open details",
        enteredBy: "Entered by",
        enteredOn: "Entered on",
        openRfis: "Open RFIs",
        extra: "Linked extra / credit",
        amountSubmitted: "Submitted amount",
        amountAccepted: "Accepted amount",
        viewInExtras: "View in Extras & Credits",
        fullHistory: "Full history",
        noneF: "None",
        noneM: "None",
        close: "Close"
      },
      issue: {
        menuLabel: "Change the directive outcome",
        backToPending: "Back to \"To assess\"",
        backHint: "To choose another outcome, go back to \"To assess\" first.",
        revertDraftMessage: "Draft extra {number} will be deleted and the directive will go back to \"To assess\".",
        revertSubmittedTitle: "Extra already submitted",
        revertSubmittedMessage: "An extra has already been submitted for this directive: {details}. Proceeding will permanently delete this extra.",
        revertDeleteConfirm: "Delete the extra and go back",
        revertBlockedTitle: "Cannot go back to \"To assess\"",
        revertBlockedApproved: "This extra is approved ({details}). Cancel it first in Extras & Credits before changing the directive outcome.",
        revertBlockedRefs: "Extra {number} cannot be deleted: {reasons}. Remove these links first before changing the directive outcome.",
        reason: {
          invoices: "linked to {count} subcontractor invoice(s)",
          paymentRequests: "included in {count} payment request line(s)",
          expenses: "linked to {count} expense(s)",
          changeOrders: "linked to {count} sent or signed change order(s)"
        },
        close: "Close",
        revertedToast: "Extra {number} deleted, directive back to \"To assess\"",
        checkError: "Could not check the linked extra. Please try again.",
        revertError: "The directive could not be set back to \"To assess\". Nothing was deleted.",
        deleteFailed: "The extra could not be deleted. The directive was restored to its previous state.",
        deleteFailedNoRestore: "The extra could not be deleted and the directive could not be restored. Check Extras & Credits."
      },
      link: {
        menuLink: "Link to an extra…",
        menuUnlink: "Detach extra (keep it)",
        link: "Link…",
        unlink: "Detach…",
        title: "Link to an existing extra",
        subtitle: "unlinked extras and credits of the project",
        search: "Search by no. or title…",
        empty: "No unlinked extra in this project.",
        noMatch: "No extra matches the search.",
        loadError: "The extras could not be loaded.",
        confirm: "Link",
        linking: "Linking…",
        linkedToast: "Extra {label} linked to the directive",
        selected: "Selected: {label}",
        alreadyLinked: "This extra was just linked to another directive. Please choose another one.",
        error: "The link could not be saved.",
        unlinkTitle: "Detach extra",
        unlinkMessage: "Extra {number} will be detached from this directive, but kept. The directive will go back to \"To assess\".",
        unlinkConfirm: "Detach",
        unlinkedToast: "Extra detached from the directive (kept)",
        unlinkError: "The link could not be removed."
      },
      filters: {
        button: "Filters",
        buttonCount: "Filters · {count}",
        status: "Status",
        professional: "Professional",
        division: "Division",
        period: "Reception period",
        allStatuses: "All statuses",
        overdue: "Overdue to assess",
        allProfessionals: "All professionals",
        allDivisions: "All divisions",
        from: "from",
        to: "to",
        reset: "Reset",
        shown: "{shown} of {total}",
        noResults: "No directive matches the filters."
      },
      rules: {
        title: "Business rules",
        r1: "The Directives tab only holds directives received from professionals.",
        r2: "Subcontractor quotes and change orders go into the linked extra.",
        r3: "Every directive ends as an extra, a credit or \"No impact\".",
        r4: "A directive \"To assess\" for {days} days or more is flagged as overdue."
      },
      updatedToast: "Directive updated",
      outcomeChangedToast: "Outcome changed",
      deletedToast: "Directive deleted",
      detachError: "Outcome saved, but extra {number} could not be detached."
    }
  },
  subInvoices: {
    resetToReceived: {
      paymentTitle: 'Payment already recorded',
      paymentMessage: 'This invoice is already linked to a payment. Do you want to void the payment(s) and set the invoice back to 100 % unpaid?',
      irreversible: 'This action cannot be undone.',
      approvalTitle: 'Invoice approved',
      approvalMessage: 'This invoice is approved (net {net}). Cancel the approval and set it back to received?',
      invoiceLabel: 'Invoice {number}',
      noNumber: 'no number',
      paymentLine: 'Check {check} · {amount} · {date}',
      paymentLineNoCheck: 'Payment · {amount} · {date}',
      approvalLine: 'Approved · net {net}',
      confirm: 'Yes',
      cancel: 'No',
      error: 'Failed to set back to received — list reloaded.',
      statusError: 'Failed to change status — list reloaded.'
    },
    overBudget: "Over budget",
    clearFilters: "Clear filters",
    filters: {
      searchPlaceholder: "Search by name or invoice #...",
      allStatuses: "All statuses",
      withBalance: "With balance",
      chooseSubcontract: "— Choose a subcontract —",
      allDp: "All payment requests",
      noDp: "No payment request"
    },
    toast: {
      markedPaid: "Marked as Paid",
      updated: "Updated"
    },
    export: {
      columns: {
        invoiceNumber: "Invoice #",
        date: "Date",
        subcontractor: "Subcontractor",
        type: "Type",
        amountTaxIn: "Amount (Tax In)",
        balance: "Balance",
        status: "Status"
      }
    },
    title: 'Received Invoices',
    subtitle: 'Manage subcontractor invoices, approvals and payments',
    kpi: {
      totalInvoices: 'Total Invoices',
      totalInvoiced: 'Total Invoiced',
      approvedToPay: 'Approved to Pay',
      paid: 'Paid'
    },
    tabs: {
      invoices: 'Invoices',
      approval: 'Invoice Approval',
      payments: 'Payments',
      summary: "Sub Summary"
    },
    summaryTab: {
      title: "Financial Summary",
      revisedContract: "Revised Contract",
      selectPrompt: "Select a subcontract to view its financial summary",
      selectHint: "Contract value, change orders, invoiced amounts and remaining balance will appear here",
      loading: "Loading financial summary…",
      contractValue: "Contract Value",
      approvedExtras: "Approved Extras",
      showBreakdown: "Show breakdown",
      hideBreakdown: "Hide breakdown",
      credits: "Credits",
      totalInvoiced: "Total Invoiced",
      overbilled: "Overbilled",
      remainingToBill: "Remaining to Bill",
      billingProgress: "Billing Progress",
      signedChangeOrders: "Signed change orders",
      noSignedChangeOrder: "No signed change order",
      changeOrderLabel: "Change order {n}"
    },
    toolbar: {
      invoices: 'invoices',
      invoice: 'invoice',
      addInvoice: 'Add Invoice'
    },
    table: {
      number: '#',
      subcontractor: 'Subcontractor',
      invoiceNumber: 'Invoice #',
      invoiceType: 'Invoice Type',
      received: 'Received',
      total: 'Total',
      status: 'Status',
      approvedPercent: 'Approved %',
      netPayable: 'Net Payable',
      created: 'Created',
      actions: 'Actions',
      approvedAmount: 'Approved Amount',
      retainage: 'Retainage',
      amountPaid: 'Amount Paid',
      paidDate: 'Paid Date',
      method: 'Method',
      reference: 'Reference',
      date: 'Date',
      amountTaxIn: 'Amount (Tax In)',
      balance: 'Balance'
    },
    status: {
      received: 'Received',
      pending_approval: 'Submitted',
      approved_to_pay: 'Approved to Pay',
      partially_paid: 'Partially Paid',
      paid: 'Paid',
      rejected: 'Rejected',
      to_assign: 'To Assign',
      draft: 'Draft',
      sent: 'Sent',
      signed: 'Signed',
      completed: 'Completed',
      cancelled: 'Cancelled',
      retentionToRelease: 'Retention to release'
    },
    actions: {
      submitForApproval: 'Submit for Approval',
      approveInvoice: 'Approve Invoice',
      recordPayment: 'Record Payment',
      payRetention: 'Pay retention',
      releaseRetention: 'Release retention',
      delete: 'Delete'
    },
    empty: {
      noInvoices: 'No sub invoices yet',
      clickToAdd: 'Click "Add Invoice" to log the first one',
      noPending: 'No invoices pending approval',
      pendingHint: 'Submit invoices for approval from the Invoices tab',
      pendingApprovalSuffix: 'pending approval',
      noPayments: 'No payments recorded yet',
      paymentsHint: 'Approved invoices will appear here for payment'
    },
    selectSubcontract: 'Select Subcontract',
    emptyTitle: 'Select a subcontract to view its invoices',
    emptySubtitle: 'Choose a subcontract above to filter the invoice list.',
    addModal: {
      title: 'Add Sub Invoice',
      subcontractorLabel: 'Subcontractor *',
      subcontractorPlaceholder: 'Select subcontractor...',
      invoiceNumberLabel: 'Invoice Number',
      invoiceDateLabel: 'Invoice Date',
      receivedDateLabel: 'Received Date',
      notesLabel: 'Notes',
      notesPlaceholder: 'Optional notes...',
      linesLabel: 'Invoice Lines',
      addLine: 'Add Line',
      addInvoice: 'Add Invoice',
      lineTypeCostCode: 'Cost Code',
      lineTypeExtra: 'Extra / Credit',
      selectCostCode: 'Select cost code...',
      selectExtra: 'Select extra/credit...',
      amountPlaceholder: 'Amount',
      descriptionPlaceholder: 'Description (optional)',
      invoiceTotal: 'Invoice Total',
      cancel: 'Cancel',
      save: 'Save Invoice',
      saving: 'Saving...',
      saveChanges: 'Save',
      errorSubcontractor: 'Please select a subcontractor.',
      errorLines: 'Add at least one invoice line.',
      errorSubcontract: 'Please select a subcontract.',
      errorExtraRequired: 'Please select an extra.',
      errorCreditRequired: 'Please select a credit.',
      errorAmount: 'All lines must have an amount > 0.',
      errorCostCode: 'Select a cost code for each cost code line.',
      errorExtra: 'Select an extra/credit for each extra line.',
      errorSave: 'Failed to save invoice.'
    },
    invoiceType: {
      subcontract: 'Subcontract',
      extra: 'Extra',
      credit: 'Credit'
    },
    approveModal: {
      title: 'Approve Invoice',
      invoiceNumber: 'Invoice #',
      invoiceTotal: 'Invoice Total',
      holdbackLabel: 'Apply 10% Holdback (Retainage)',
      approvedAmount: 'Approved Amount (100%)',
      retainage: 'Retainage',
      netPayable: 'Net Payable',
      paymentNoteLabel: 'Payment Note (optional)',
      clientPaymentRequestNumber: 'Client Payment Request #',
      enterClientPaymentRequestNumber: 'e.g. 1001',
      cancel: 'Cancel',
      approve: 'Approve to Pay',
      approving: 'Approving...',
      dpNotPaid: 'PR {number} has not been paid by the client yet.',
      approveAnyway: 'Approve anyway',
      errorApprove: 'Failed to approve.',
      errorValidationNote: 'Please explain why this approval is valid despite exceeding earned value.',
      errorAcknowledge: 'Please acknowledge the overpayment risk warning before proceeding.',
      validation: {
        requiredClientPaymentRequestNumber: 'Client Payment Request # is required and must be a positive integer.'
      }
    },
    paymentModal: {
      title: 'Record Payment',
      invoiceTotalTaxIn: 'Invoice Total (Tax In)',
      approvedAmount: 'Approved Amount',
      alreadyPaid: 'Total Paid',
      remainingApproved: 'Remaining Approved',
      solde: 'Solde',
      remaining: 'Remaining',
      checkNumberLabel: 'Check #',
      checkNumberPlaceholder: 'e.g. 1042',
      amountPaidLabel: 'Amount Paid',
      paidDateLabel: 'Paid Date',
      methodLabel: 'Method',
      referenceLabel: 'Reference',
      referencePlaceholder: 'Ref #',
      cancel: 'Cancel',
      save: 'Record Payment',
      saving: 'Saving...',
      errorAmount: 'Amount paid must be greater than 0.',
      errorCheckNumber: 'Check # is required.',
      errorReference: 'Reference is required.',
      errorExceedsApproved: 'Payment cannot exceed approved amount.',
      errorSave: 'Failed to record payment.'
    }
  },
  factureApproval: {
    title: 'Invoice Approval',
    notFound: 'Invoice not found.',
    noPdf: 'No linked PDF',
    noPdfHint: 'No source document found for this invoice.',
    summary: 'Summary',
    invoiceDate: 'Date',
    subtotal: 'Subtotal',
    tps: 'TPS',
    tvq: 'TVQ',
    totalTaxIn: 'Total (taxes incl.)',
    linkedDp: 'Linked DP',
    retentionIncludedBadge: 'Retention included by the subcontractor at entry',
    retentionExcludedBadge: 'Retention excluded at entry',
    noteLabel: 'Note',
    notePlaceholder: 'Add a note...',
    approvalControls: 'Approval Controls',
    applyRetentionLabel: 'Apply retention',
    retentionPctLabel: 'Retention (%)',
    approveButton: 'Approve to Pay',
    back: 'Back',
    invoiceLabel: 'Invoice',
    retentionRelease: {
      title: 'Retention release',
      retentionToRelease: 'Retention to release',
      remaining: 'Remaining to pay',
      noContract: 'This invoice is not linked to a contract — release not possible here.',
      quittanceRequired: 'A signed final waiver is required to release the retention.',
      quittanceLink: 'Open Notices & Waivers',
      approveButton: 'Approve retention payment',
      releasing: 'Releasing...',
      confirmTitle: 'Release the retention?',
      confirmMessage: 'Confirm that you have received all documents required to release the retention.',
      confirmButton: 'Confirm',
      error: 'Error while releasing the retention.'
    }
  },
  expenses: {
    title: 'Expenses',
    subtitle: 'Track project expenses per cost code',
    addExpense: 'Add Expense',
    kpi: {
      totalExpenses: 'Total Expenses',
      totalAmount: 'Total Amount',
      approved: 'Approved',
      paid: 'Paid'
    },
    toolbar: {
      expenses: 'expenses',
      expense: 'expense',
      searchPlaceholder: 'Search an expense…',
      exportCsvTooltip: 'Export all visible rows to CSV',
      exportPdfTooltip: 'Export all visible rows to PDF'
    },
    table: {
      date: 'Date',
      vendor: 'Vendor',
      description: 'Description',
      division: 'Division',
      costCode: 'Cost Code',
      costItem: 'Cost Item',
      dp: 'PR',
      amount: 'Amount',
      status: 'Status',
      actions: 'Actions',
      extraBadge: 'Extra',
      taxPrefix: 'tax:'
    },
    status: {
      draft: 'Draft',
      approved: 'Approved',
      paid: 'Paid'
    },
    empty: {
      noExpenses: 'No expenses yet',
      clickToAdd: 'Click "+ Add Expense" to log the first one'
    },
    modal: {
      addTitle: 'Add Expense',
      editTitle: 'Edit Expense',
      dateLabel: 'Date',
      vendorLabel: 'Vendor',
      vendorPlaceholder: 'e.g. Home Depot',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'e.g. Lumber and fasteners',
      divisionLabel: 'Division',
      divisionPlaceholder: 'Select division...',
      costCodeLabel: 'Cost Code',
      costCodePlaceholder: 'Select cost code...',
      extraLabel: 'Extra / Credit (optional)',
      extraPlaceholder: 'Select extra/credit...',
      amountLabel: 'Amount',
      tpsLabel: 'TPS',
      tvqLabel: 'TVQ',
      totalLabel: 'Total (auto-calculated)',
      referenceLabel: 'Reference #',
      referencePlaceholder: 'e.g. INV-001',
      statusLabel: 'Status',
      paidByLabel: 'Paid by',
      paidByPlaceholder: 'Select payment method',
      addExpense: 'Add Expense',
      dpLabel: 'DP',
      dpNoneOption: '— None —',
      dpApproved: 'approved',
      dpSubmitted: 'submitted',
      divisionGroupConstruction: 'Construction cost',
      divisionGroupGeneral: 'General cost',
      divisionGroupAdmin: 'Admin & Profit',
      extraOptionPrefix: 'Extra',
      paidByCheque: 'Cheque',
      prefilledFromInbox: 'Pre-filled from inbox document'
    },
    validation: {
      dateRequired: 'Please select a date.',
      vendorRequired: 'Vendor is required.',
      amountRequired: 'Amount must be greater than 0.',
      costCodeOrExtraRequired: 'Please select a cost code or an extra.',
      saveFailed: 'Failed to save expense.',
      paymentRequestRequired: 'Select a payment request'
    },
    rowActions: {
      documents: 'Documents',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      noDocuments: 'No documents',
      moreDocuments: '+{count} more'
    },
    bulk: {
      selected: '{count} selected',
      exportCsv: 'Export CSV',
      exportPdf: 'Export PDF',
      clear: 'Clear'
    },
    inbox: {
      bannerLabel: 'Document pre-loaded from inbox:',
      documentFallback: 'Document'
    },
    delete: {
      title: 'Delete Expense',
      message: 'Are you sure? This cannot be undone.'
    },
    export: {
      reportTitle: 'Expenses Report',
      badge: 'Expenses',
      generated: 'Generated {date}',
      countOne: '{count} expense',
      countMany: '{count} expenses',
      grandTotal: 'Grand Total',
      projectFallback: 'Project',
      selectedSuffix: 'Selected',
      columns: {
        number: '#',
        date: 'Date',
        vendor: 'Vendor',
        description: 'Description',
        costItem: 'Cost Item',
      dp: 'PR',
        amount: 'Amount',
        tax: 'Tax',
        total: 'Total',
        status: 'Status',
        paidBy: 'Paid By',
        reference: 'Reference'
      }
    }
  },
  extra: {
    enterAcceptedCostTitleCredit: "Enter the accepted credit amount",
    creditLabel: "Credit",
    exportAcceptedNote: "Accepted total: approved extras and credits only.",
    selectSubcontractorRequired: "Select a subcontractor",
    errorSubcontractorRequired: "Every group must have a subcontractor or be marked as internal work",
    internalOnlyTitle: "Internal work",
    internalOnlyBody: "This extra has no subcontractor lines, so no change order is needed. Approving records the accepted amount without committing any subcontractor.",
    approveWithoutAvenant: "Approve without a change order",
    noLinesError: "This extra has no usable lines. Edit it and assign a subcontractor to each line, or mark them as internal work, then try again.",
    enterAcceptedCostTitle: "Enter Accepted Cost",
    enterAcceptedCostDescription: "Please enter the accepted cost before approving this extra.",
    extraLabel: "Extra",
    coLabel: "CO #",
    submittedCost: "Submitted Cost",
    acceptedCost: "Accepted Cost",
    errorGreaterThanZero: "Please enter a value greater than 0."
  },
  documents: {
    delete: {
      title: "Delete document",
      message: "Are you sure you want to delete this file?",
      cancel: "Cancel",
      confirm: "Delete"
    },
    title: "Documents",
    subtitle: "Manage project files and folders",
    searchPlaceholder: "Search documents...",
    foldersPanel: "Folders",
    newFolderShort: "New",
    noFoldersYet: "No folders yet",
    createFirstFolder: "Create your first folder",
    selectAFolder: "Select a folder",
    selectFolderToViewFiles: "Select a folder to view files",
    uploadFile: "Upload file",
    uploading: "Uploading…",
    noFilesYet: "No files yet",
    uploadToGetStarted: "Upload a file to get started",
    noDocumentsAttached: "No documents attached yet",
    loading: "Loading files…",
    duplicateName: "A document named \"{name}\" already exists.",
    duplicatesSkipped: "Skipped, already attached: {names}",
    uploadProgress: "{done} of {total} uploaded",
    done: "Done",
    deleteFileAction: "Delete file",
    dropFilesPrefix: "Drop files here or",
    dropFilesAction: "click to upload",
    supportedFormats: "PDF, Excel, Word supported · Max 20MB each",
    errorDeleting: "Unexpected error deleting file.",
    errorOpening: "Unexpected error opening file.",
    errorCouldNotDelete: "Could not delete file: {message}",
    errorCouldNotOpen: "Could not open file: {message}",
    errorFileTooLarge: "File too large (max 20MB)",
    errorUnexpected: "Unexpected error",
    renameAction: "Rename",
    downloadAction: "Download",
    deleteAction: "Delete",
    confirmAction: "Confirm",
    cancelAction: "Cancel",
    preview: {
      download: "Download",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      loadingFile: "Loading file…",
      unableToLoad: "Unable to load file",
      openInNewTab: "Open in new tab",
      notAvailable: "Preview not available",
      downloadFile: "Download file"
    },
    createFolder: {
      title: "Create Folder",
      subtitle: "Add a new folder to organize your documents",
      nameLabel: "Folder Name",
      namePlaceholder: "e.g. Drawings, Permits, Reports…",
      typeLabel: "Folder Type",
      rootOption: "New Folder",
      subfolderOption: "Subfolder",
      insideLabel: "Inside:",
      selectFolderFirst: "Select a folder first to create a subfolder",
      submit: "Create Folder"
    },
    renameFolder: {
      title: "Rename Folder"
    },
    confirmDelete: {
      folderTitle: "Delete Folder",
      fileTitle: "Delete File",
      folderMessage: "This will permanently delete the folder and all its contents.",
      fileMessage: "This will permanently delete the file."
    }
  },
  changeOrders: {
    delete: {
      title: "Delete change order",
      message: "Are you sure you want to delete this change order? This action cannot be undone.",
      confirm: "Delete",
      cancel: "Cancel"
    },
    kpi: {
      total: "Total COs",
      pending: "Pending (Sent)",
      approved: "Approved",
      approvedAmount: "Approved Amount",
    },
    filters: {
      allStatuses: "All statuses"
    },
    addButton: "Add Change Order",
    allSubcontractors: "All",
    unknownSubcontractor: "Unknown",
    table: {
      coNumber: "Sub. Co#",
      subcontractor: "Subcontractor",
      nCo: "Directive #",
      avenantTitle: "Change order title",
      amount: "Amount",
      status: "Status",
      notes: "Notes",
      actions: "Actions",
    },
    status: {
      draft: "Draft",
      sent: "Sent",
      signed: "Signed",
      rejected: "Rejected",
    },
    actions: {
      changeStatus: "Change status",
      actions: "Actions",
      documents: "Documents",
      deleteFile: "Delete file",
      editNote: "Edit note",
      addNote: "Add note",
    },
    documents: {
      panelTitle: "Documents",
      none: "No documents",
      noneAttached: "No documents attached yet",
      uploading: "Uploading…",
    },
    empty: {
      title: "No change orders yet",
      hint: "Click « + Add Change Order » to create one",
    },
  },
  subcontracts: {
    delete: {
      title: "Delete contract",
      message: "Are you sure you want to delete this contract? This action cannot be undone.",
      confirm: "Delete",
      cancel: "Cancel",
      label: "Contract",
      blockedTitle: "Cannot delete this contract",
      blockedIntro: "This contract is linked to existing records. Delete or detach them first.",
      blockedClose: "Close",
      blockedAvenants: "{count} change order(s)",
      blockedSubInvoices: "{count} subcontractor invoice(s)",
      blockedSubInvoiceLines: "{count} invoice line(s)",
      blockedExtras: "{count} extra(s)",
      blockedExtraLines: "{count} extra line(s)",
      blockedCorrections: "{count} cost code correction(s)",
      blockedQuittances: "{count} release(s)",
      checking: "Checking links…",
      success: "Contract deleted",
      error: "Could not delete the contract. Please try again."
    }
  },
  changeOrder: {
    note: {
      title: "Change Order Note",
      placeholder: "Add a note for this change order..."
    },
    page: {
      title: "Change Orders",
      subtitle: "Subcontract Change Order Tracking"
    },
    detail: {
      title: "Change order details",
      view: "View details",
      subtotal: "Subtotal",
      tps: "GST (5%)",
      tvq: "QST (9.975%)",
      total: "Total",
      notes: "Notes",
      close: "Close"
    }
  },
  avenants: {
    filteredBySubcontract: "Filtered by subcontract",
    clearFilter: "Clear filter",
    detail: {
      title: "Change order details",
      view: "View details",
      subtotal: "Subtotal",
      tps: "GST (5%)",
      tvq: "QST (9.975%)",
      total: "Total",
      notes: "Notes",
      close: "Close",
      subcontractor: "Subcontractor",
      coNumber: "Change order #",
      sourceExtra: "Source extra",
      directive: "Directive",
      directiveNumber: "Directive number",
      avenantTitle: "Change order title",
      description: "Description",
      status: "Status",
      eyebrow: "Change order",
      editMode: "Editing",
      infoSection: "Information",
      contentSection: "Content",
      amountSection: "Amount",
      category: "Category",
      subcontractPlaceholder: "Choose a subcontract…",
      descriptionPlaceholder: "Describe the change order…",
      subcontractRequired: "Subcontract is required",
      amountRequired: "Valid amount required",
      saveError: "Could not save the change order.",
      save: "Save",
      saving: "Saving…",
      discardTitle: "Discard changes?",
      discardMessage: "Unsaved changes will be lost.",
      discardConfirm: "Discard",
      keepEditing: "Keep editing",
      division: "Division",
      inherited: "Inherited from the source extra",
      subcontractLocked: "Locked: the change order has been sent or signed.",
      statusLabels: {
        draft: "Draft",
        sent: "Sent",
        signed: "Signed",
        rejected: "Rejected"
      }
    },
    form: {
      addTitle: "Add Change Order",
      editTitle: "Edit Change Order",
      addSubtitle: "Create a new subcontract change order",
      editSubtitle: "Update the change order details below",
      create: "Create Change Order",
      saveChanges: "Save Changes",
      sourceInfo: "Source extra info",
      sourceExtraTitle: "Source extra title",
      directive: "Directive",
      directiveNumber: "Directive number (client)",
      category: "Category"
    }
  },
  paymentRequests: {
    errors: {
      saveBeforeAttach: "Save the request first to attach documents."
    },
    title: "Payment Requests",
    subtitle: "Manage client payment requests",
    exportExcel: "Excel",
    addRequest: "Add Payment Request",
    searchPlaceholder: "Search payment requests...",
    kpi: {
      totalRequests: "Total Requests",
      totalValue: "Total Value",
      invoiced: "Invoiced",
      paid: "Paid",
    },
    table: {
      requestNumber: "Request #",
      date: "Date",
      status: "Status",
      totalAmount: "Total Amount",
      beforeTax: "Before tax",
      modifiedBy: "Modified by",
      lastModified: "Last modified",
      actions: "Actions",
      rowSubtitle: "Payment request",
      paidOn: "Received {date}",
      billing: "Billing"
    },
    actions: {
      edit: "Edit",
      delete: "Delete",
      viewDocuments: "View Documents"
    },
    modal: {
      titleCreate: "New Payment Request",
      titleEdit: "Edit Payment Request",
      dp1Notice: "First request — define base lines",
      dp2Notice: "Following request — previous amounts locked",
      baseLines: "Contract Lines",
      extrasSection: "Extras",
      addLine: "+ Add Line",
      addExtraLine: "+ Add Extra Line",
      description: "Description",
      contractAmount: "Contract Amount",
      percent: "%",
      valueToDate: "Value to Date",
      previous: "Previous",
      thisRequest: "This Request",
      documents: "Documents (optional)",
      dropFilesHere: "Drop files here or click to upload",
      summary: "Summary",
      subtotal: "Subtotal",
      retention: "Holdback ({rate}%)",
      applyHoldback: "Apply 10% holdback",
      subtotalAfterHoldback: "Subtotal after holdback",
      tps: "TPS (5%)",
      tvq: "TVQ (9.975%)",
      total: "Total",
      contractTotal: "Contract Total",
      extrasTotal: "Extras Total",
      revisedContract: "Revised Contract",
      baseContractAmount: "Base contract amount",
      approvedExtras: "Approved extras",
      revisedContractAmount: "Revised contract amount",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      statusLabel: "Status",
      periodSection: "Period",
      periodStartLabel: "Period start",
      periodEndLabel: "Period end",
      periodInvalid: "End date must be after start date"
    },
    status: {
      draft: "Draft",
      submitted: "Submitted",
      approved: "Approved",
      paid: "Payment received"
    },
    billing: {
      notInvoiced: "Not invoiced",
      invoiced: "Invoiced",
      paid: "Paid",
      markInvoiced: "Mark as invoiced",
      markPaid: "Payment received"
    },
    invoiceDialog: {
      title: "Invoice — PR-{number}",
      number: "Invoice number",
      date: "Invoice date",
      numberRequired: "Enter the invoice number.",
      dateRequired: "Enter the invoice date.",
      error: "The invoice was not saved. Try again.",
      cancel: "Cancel",
      confirm: "Save"
    },
    paidDialog: {
      title: "Payment received — PR-{number}",
      date: "Date received",
      amount: "Amount received (optional)",
      dateRequired: "Enter the date the payment was received.",
      amountInvalid: "Invalid amount.",
      error: "The status was not saved. Try again.",
      cancel: "Cancel",
      confirm: "Save"
    },
    validation: {
      negativeValue: "Value cannot be negative",
      exceedsContract: "Cannot exceed contract amount",
      descriptionRequired: "Description is required"
    },
    extras: {
      selectExtra: "Select an approved extra...",
      credit: "Credit",
      alreadySubmitted: "Already submitted in previous request"
    },
    view: {
      title: "PAYMENT REQUEST",
      period: "Period",
      amounts: "Amounts",
      amountBeforeTax: "Amount before tax",
      close: "Close",
      exportPdf: "Export PDF",
      exportExcel: "Excel",
    },
    empty: "No payment requests yet. Click \"+ Add Payment Request\" to create the first one.",
    deleteBlockedTitle: "Cannot delete",
    deleteBlockedMessage: "{invoices} invoice(s) and {expenses} expense(s) are linked to this DP — relink them to another DP before deleting.",
    deleteBlockedApproved: "DP{number} is approved — it is closed and cannot be deleted.",
    deleteBlockedNewerExists: "Cannot delete DP{number}: a more recent request exists (DP{max}). Delete the later requests first.",
    deleteConfirmTitle: "Delete DP{number}?",
    deleteConfirmMessage: "The lines you added to this payment request will be deleted. This action cannot be undone.",
    deleteFailed: "Could not delete this payment request. Please try again.",
    editBlockedApproved: "DP{number} is approved — it is closed (read only) and cannot be modified.",
    closedReadOnly: "Closed — read only",
    saveBlockedApproved: "This payment request is approved and cannot be modified.",
    blockedCreate: {
      title: "Previous request incomplete",
      message: "Payment request DP{number} is still a draft. Submit or approve it before creating a new one.",
      close: "Close"
    },
    approveRequest: {
      title: "Approve request DP{number}",
      downloadUnsigned: "Download PDF (unsigned)",
      dropZoneLabel: "Drop the signed PDF here (client/professional)",
      dropZoneHint: "Click or drag and drop — PDF only",
      pdfOnly: "Only PDF files are accepted.",
      approve: "Approve",
      error: "An error occurred. The request was not approved."
    },
    toolbar: {
      downloadDetail: "Download detail",
      downloadDetailCount: "Download detail ({count})",
      selectARequest: "Select a request",
      exportDetailExcel: "Export DP{number} to Excel",
      exportPdf: "Export PDF"
    },
    export: {
      projectFallback: "Project",
      exportedOn: "Exported {date}",
      totalItems: "Total ({count} items)",
      columns: {
        requestNumber: "Request #",
        date: "Date",
        total: "Total",
        status: "Status"
      }
    },
    excelDetail: {
      sheetName: "Payment Request",
      projectLabel: "Project",
      clientLabel: "Client",
      contractorLabel: "Contractor",
      periodLabel: "Period",
      contractLines: "Contract Lines",
      extras: "Extras",
      summary: "Summary",
      total: "TOTAL",
      uploadFailed: "Upload failed",
      columns: {
        description: "Description",
        contractAmount: "Contract Amount",
        previous: "Previous",
        thisRequest: "This Request",
        monthly: "Monthly"
      }
    },
    pdf: {
      title: "PAYMENT REQUEST",
      sections: {
        project: "PROJECT",
        client: "CLIENT",
        detail: "DETAIL",
        totals: "TOTALS"
      },
      columns: {
        description: "Description",
        contractAmount: "Contract Amount",
        previous: "Previous",
        thisMonth: "This Month",
        toDate: "To Date"
      },
      totals: {
        subtotal: "Subtotal",
        holdback: "Holdback (10%)",
        subtotalAfterHoldback: "Subtotal after holdback",
        tps: "TPS (5%)",
        tvq: "TVQ (9.975%)",
        netTotal: "Net Total"
      }
    }
  },
  invoiceInbox: {
    title: "Invoice Inbox",
    subtitle: "Upload and manage incoming invoices",
    uploadButton: "Upload Invoice",
    uploading: "Uploading...",
    searchPlaceholder: "Search by file name...",
    noInvoicesTitle: "No invoices found",
    noInvoicesSubtitle: "Upload a PDF to get started",
    noFileUrl: "No file URL available",
    loadingDocument: "Loading document...",
    unableToLoad: "Unable to load document",
    unableToLoadSub: "The file could not be displayed in the viewer",
    downloadFile: "Download file",
    openInNewTab: "Open in new tab",
    uploadError: "Upload failed",
    deleteErrors: {
      notPermitted: "Delete refused: your account is not allowed to delete inbox entries. The entry is still there.",
      partial: "{deleted} entry(ies) deleted, {failed} refused. Refused entries stay selected.",
      failed: "The delete failed. The entry was not removed."
    },
    invoiceCreated: "Created",
    invoiceCount: "{count} invoice",
    invoiceCountPlural: "{count} invoices",
    upload: {
      done: "Upload complete",
      inProgress: "Uploading invoices...",
      accepted: "{count} accepted",
      rejected: "{count} rejected",
      addedOne: "1 invoice added",
      added: "{count} invoices added",
      skippedOne: "1 skipped (duplicate)",
      skipped: "{count} skipped (duplicates)",
      processing: "Processing {current} of {total}...",
      skippedTitle: "Skipped files",
      reason: {
        duplicate: "Already exists"
      }
    },
    filter: {
      title: "Filter Invoices",
      invoicesLabel: "Invoices",
      inbox: "Inbox (New)",
      new: "New",
      ready: "Ready",
      created: "Created",
      uploaderMine: "My invoices",
      uploaderAll: "All uploaders"
    },
    columns: {
      file: "File",
      supplier: "Supplier",
      date: "Date",
      amount: "Amount",
      status: "Status"
    },
    status: {
      new: "New",
      ready: "Ready",
      created: "Created"
    },
    form: {
      createInvoice: "Create Invoice",
      cancel: "Cancel",
      panelTitle: "Create Invoice",
      prevInvoice: "Previous invoice",
      nextInvoice: "Next invoice",
      reRunExtraction: "Re-run AI extraction",
      remainingLeft: "{count} left",
      ai: {
        extractionInProgress: "AI extraction in progress…",
        extractionSubtitle: "Reading document and extracting invoice data. This may take a few seconds.",
        matchingSupplier: "AI matching supplier to subcontractors...",
        matchingCostCode: "AI selecting best cost code for this trade...",
        supplierLabel: "Supplier:",
        matchedLabel: "Matched:",
        confidenceLabel: "{pct}% confidence",
        possibleMatch: "Possible match ({pct}% confidence):",
        acceptMatch: "Accept this match",
        amountDetected: "Amount detected:",
        fieldsPreFilled: "Fields pre-filled from document — please verify before submitting.",
        extractionFailed: "AI could not extract data from this document. Please fill in the fields manually.",
        reExtractionFailed: "Re-extraction failed. Please fill in the fields manually."
      },
      project: {
        label: "Project",
        autoSelected: "Auto-selected",
        placeholder: "— Select a project —",
        loading: "Loading project data..."
      },
      typeToggle: {
        label: "Type",
        invoice: "Invoice",
        expense: "Expense"
      },
      invoice: {
        subcontractLabel: "Subcontract",
        searchSubcontract: "Search subcontract...",
        searchExtras: "Search extras...",
        searchCredits: "Search credits...",
        invoiceNumberLabel: "Invoice #",
        invoiceNumberPlaceholder: "e.g. INV-2024-001",
        dpLabel: "DP",
        noneOption: "— None —",
        dpApproved: "approved",
        dpSubmitted: "submitted",
        lineTypeCostCode: "Cost Code",
        lineTypeExtraCredit: "Extra / Credit",
        subcontractOptional: "Subcontract",
        subcontractOptionalSuffix: "(optional)",
        selectSubcontractOptional: "Select subcontract (optional)...",
        descriptionOptional: "Description (optional)",
        notesLabel: "Notes",
        notesPlaceholder: "Optional notes...",
        processingLabel: "Processing...",
        retentionIncluded: "Retention included",
        retentionIncludedHint: "The subcontractor has already included the retention (10%) in their invoice.",
        applyRetention: "Apply retention",
        retentionPct: "Retention (%)",
        approvedAmount: "Approved amount (100%)",
        retentionLine: "Retention ({pct}%)",
        netPayable: "Net payable"
      },
      expense: {
        vendorLabel: "Vendor",
        vendorPlaceholder: "e.g. Home Depot",
        expenseDateLabel: "Expense Date",
        referenceLabel: "Reference #",
        referencePlaceholder: "e.g. REC-001",
        amountLabel: "Amount",
        subtotalAfterHoldback: "Subtotal after holdback",
        tpsLabel: "TPS (5%)",
        tvqLabel: "TVQ (9.975%)",
        autoPlaceholder: "Auto",
        taxesIncluded: "Taxes included",
        taxesIncludedHint: "The entered amount includes taxes — pre-tax amount is calculated automatically.",
        totalLabel: "Total",
        statusLabel: "Status",
        statusPaid: "paid",
        statusPending: "pending",
        statusApproved: "approved",
        divisionLabel: "Division",
        selectDivision: "— Select a division —",
        groupConstruction: "Construction cost",
        groupGeneral: "General cost",
        groupAdmin: "Admin & Profit",
        costCodeLabel: "Cost Code",
        selectCostCodePlaceholder: "— Select a cost code —",
        selectDivisionFirst: "— Select a division first —",
        extraCreditLabel: "Extra / Credit",
        optionalSuffix: "(optional)",
        paidByLabel: "Paid by",
        selectPlaceholder: "— Select —",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Optional description...",
        createExpenseButton: "Create Expense",
        dpLabel: "DP",
        dpNoneOption: "— None —",
        dpApproved: "approved",
        dpSubmitted: "submitted"
      },
      errors: {
        selectProject: "Please select a project.",
        enterVendor: "Please enter a vendor name.",
        enterAmount: "Please enter a valid amount.",
        selectSubcontract: "Please select a subcontract.",
        selectExtra: "Please select an extra.",
        selectCredit: "Please select a credit.",
        addLine: "Please add at least one line.",
        lineAmount: "All lines must have a valid amount.",
        lineCostCode: "Please select a cost code for each line.",
        lineExtraCredit: "Please select an extra/credit for each line.",
        createExpense: "Failed to create expense.",
        createInvoice: "Failed to create invoice.",
                paymentRequestRequired: "Select a payment request"
      }
    },
    splitView: {
      unnamedFile: "Invoice PDF",
      closePanel: "Close"
    }
  },
  duplicateInvoiceModal: {
    title: "Duplicate Invoice",
    message: "An invoice with the same number already exists for this subcontractor and project.",
    existingLabel: "The one already on file",
    incomingLabel: "The one you are about to destroy",
    invoiceNumber: "Invoice #",
    date: "Date",
    status: "Status",
    createdAt: "Created",
    amount: "Amount",
    subcontractor: "Subcontractor",
    project: "Project",
    fileLabel: "File",
    receivedFrom: "Received from",
    receivedAt: "Received on",
    existingNotLoaded: "The existing invoice could not be loaded. Check it before deleting anything.",
    cancel: "Cancel",
    viewExisting: "View existing",
    delete: "Delete the received document"
  },
  duplicateExpenseModal: {
    title: "Possible Duplicate",
    message: "An expense with the same reference number and vendor already exists for this project.",
    existingLabel: "Existing expense:",
    invoiceNumber: "Reference #",
    date: "Date",
    amount: "Amount",
    vendor: "Vendor",
    cancel: "Cancel",
    saveAnyway: "Save anyway"
  },
  invoice: {
    form: {
      title: "Add Invoice",
      editTitle: "Edit Invoice",
      sectionDetails: "Invoice Details",
      sectionNotes: "Notes",
      invoiceType: "Invoice Type",
      subcontract: "Subcontract",
      extra: "Extra",
      credit: "Credit",
      selectSubcontract: "Select Subcontract",
      invoiceNumber: "Invoice Number",
      paymentRequest: "Payment Request",
      dpApproved: "approved",
      dpSubmitted: "submitted",
      invoiceDate: "Invoice Date",
      receivedDate: "Received Date",
      invoiceLines: "Invoice Lines",
      costCode: "Cost Code",
      selectCostCode: "Select cost code...",
      amount: "Amount",
      description: "Description (optional)",
      subtotal: "Subtotal",
      grandTotal: "Grand Total",
      applyTaxes: "Apply Québec Taxes",
      cancel: "Cancel",
      save: "Save Invoice",
      addLine: "Add Line"
    }
  },
  teamSchedule: {
    title: "Crew Schedule",
    subtitle: "Who works on which site, and when.",
    today: "Today",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    allProjects: "All projects",
    allTrades: "All trades",
    assign: "Assign",
    addHere: "assign",
    moreOthers: "+{count} more",
    stats: {
      workers: "Workers",
      people: "People",
      personDays: "Person-days",
      projects: "Sites covered",
      unassigned: "Unassigned this week",
    },
    groups: {
      workers: "Workers",
      supervisors: "Supervisors",
    },
    view: {
      month: "Month",
      week: "Week",
    },
    durations: {
      half: "4 h · half day",
      full: "8 h · full day",
      custom: "Other · I pick the end",
    },
    weekdays: {
      "0": "S",
      "1": "M",
      "2": "T",
      "3": "W",
      "4": "T",
      "5": "F",
      "6": "S",
    },
    result: {
      title: "Result",
      created: "{count} assignments created",
      skipped: "{count} days skipped — {worker} was already booked at those hours:",
      alreadyAt: "already {time} at {project}",
    },
    modal: {
      createTitle: "New assignment",
      editTitle: "Edit assignment",
      worker: "Worker",
      workerPlaceholder: "Choose a worker…",
      project: "Project",
      projectPlaceholder: "Choose a project…",
      date: "Date",
      block: "Block",
      startTime: "Start",
      endTime: "End",
      note: "Note for the worker",
      notePlaceholder: "Here are the tasks to do…",
      when: "When",
      singleDay: "One day",
      multipleDays: "Several days",
      fromDate: "From",
      toDate: "To",
      from: "From",
      to: "To",
      startTime: "Start time",
      duration: "Duration",
      weekendHint: "Weekends are excluded by default.",
      summaryRange: "{count} days · {start} → {end} each",
      createCount: "Create {count} assignments",
      conflict: "{worker} is already assigned to {project} from {time} on {date}. Remove that assignment first, or pick another time range.",
    },
    empty: {
      title: "No one to schedule yet",
      message: "Add employees with the Worker or Superintendent role under Users, then come back here to build the schedule.",
    },
    errors: {
      duplicate: "That person already has this block on that day.",
      saveFailed: "Saving failed.",
      deleteFailed: "Deleting failed.",
      overlap: "That range overlaps another one for this person.",
      overlapRace: "Someone else just booked those hours. Reload the page and try again.",
      overlapNamed: "{worker} is already assigned to {project} from {time} on {date}.",
      checkFailed: "The end time must be after the start time.",
      forbidden: "You are not allowed to change this schedule.",
      saveFailedCode: "Saving failed ({code}).",
      endBeforeStart: "The end time must be after the start time.",
    },
  },
  schedule: {
    title: "Schedule",
    reorder: {
      moveUp: "Move up",
      moveDown: "Move down"
    },
    zoom: {
      today: "Today",
      day: "Day",
      week: "Week",
      month: "Month"
    },
    subtitle: "Schedule subcontractors and track work progress",
    viewGantt: "Gantt",
    viewWeek: "Week",
    addTask: "Add Task",
    addSubtask: "Add Subtask",
    noTasks: "No tasks yet",
    noTasksHint: "Click \"Add Task\" to create your first task.",
    days: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
    table: {
      task: "Task",
      assigned: "Assigned",
      status: "Status",
      start: "Start",
      end: "End",
      duration: "Dur.",
    },
    week: {
      previous: "Previous week",
      next: "Next week",
      noWork: "No work",
      noSubcontractor: "No Subcontractor",
    },
    tooltips: {
      dragToConnect: "Drag to connect",
      clickToEdit: "Double-click to edit",
      clickToEditDuration: "Double-click to edit duration",
      dragToResizeColumn: "Drag to resize column",
      dragToResizePanels: "Drag to resize panels",
      collapse: "Collapse",
      expand: "Expand",
    },
    modal: {
      createTask: "Add Task",
      createSubtask: "Add Subtask",
      editTask: "Edit Task",
      subtaskOf: "Subtask of:",
      parentTask: "Parent Task",
      taskName: "Task Name",
      taskNamePlaceholder: "e.g. Foundation Excavation",
      subcontractor: "Subcontractor",
      startDate: "Start Date",
      endDate: "End Date",
      progress: "Progress (%)",
      cancel: "Cancel",
      creating: "Creating…",
      saving: "Saving…",
      saveChanges: "Save Changes",
      optional: "optional"
    },
    errors: {
      nameRequired: "Task name is required.",
      startRequired: "Start date is required.",
      endRequired: "End date is required.",
      endBeforeStart: "End date must be after start date.",
      unexpected: "An unexpected error occurred.",
      createFailed: "Failed to create task.",
      updateFailed: "Failed to update task."
    }
  },
  subcontractCombobox: {
    selectProjectFirst: "Select a project first"
  },
  expenseRedirect: {
    title: "Select a Project",
    subtitle: "Choose the project this expense belongs to"
  },
  costCodeNote: {
    errors: {
      load: "Failed to load note.",
      save: "Failed to save note. Please try again."
    },
    title: "Note",
    placeholder: "Capture estimation analysis, assumptions, scope context...",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    iconTooltipAdd: "Add estimation note",
    iconTooltipEdit: "Edit estimation note"
  },
  projectTeam: {
    sectionTitle: "Project Team",
    pm: "Project Manager",
    supervisor: "Supervisor",
    coordinator: "Coordinator",
    assistant: "Assistant",
    selectUser: "Select a user...",
    noUsersAvailable: "No users available",
    notAssigned: "Not assigned",
    picker: {
      placeholder: "Select a user...",
      searchPlaceholder: "Search by name or email",
      noUsers: "No active users found",
      loading: "Loading users..."
    }
  },
  internal: {
    suffix: "(internal)"
  },
  projectWorkspace: {
    accessDenied: {
      title: "Project not found or access denied",
      message: "This project doesn't exist or you don't have permission to view it. Contact your administrator if you believe this is an error.",
      backButton: "Back to Project List"
    }
  },
  dailyLog: {
    title: "Daily Logs",
    subtitle: "Track daily site activity and conditions",
    newLog: "New Log",
    empty: "No daily logs yet. Create your first log to track site activity.",
    accessDenied: "You don't have access to daily logs on this project.",
    list: {
      date: "Date",
      createdBy: "Created by",
      editedBy: "Last edited by",
      crewCount: "{count} trade(s) on site",
      photosCount: "{count} photo(s)",
      moreCount: "+{count} more"
    },
    badge: {
      missing: "Missing log",
      missingDays: "Missing {count} days"
    },
    modal: {
      titleCreate: "New Daily Log",
      titleEdit: "Edit Daily Log",
      date: "Date",
      datePlaceholder: "Select date",
      dateAlreadyExists: "A log already exists for this date",
      dateFuture: "The date cannot be in the future.",
      dateLockedApproved: "Approved log: remove the approval to change the date.",
      dateBeforeSource: "This log carries tasks over from the log of {date}: its date must come after.",
      dateAfterDependent: "The log of {date} carries tasks over from this one: its date must come before.",
      datePrefillReplace: "The crew and carried-over tasks depend on the date. Replace the ones you entered with those from the log before this date?",
      datePrefillReplaceConfirm: "Replace",
      lastLog: "Last log: {date}",
      noLogYet: "No log for this project yet.",
      missingDays: "Working days without a log (last 30 days): {dates}",
      save: "Save",
      cancel: "Cancel",
      saving: "Saving..."
    },
    weather: {
      label: "Weather",
      condition: "Condition",
      conditionPlaceholder: "Select condition",
      temperature: "Temperature (°C)",
      sunny: "Sunny",
      cloudy: "Cloudy",
      rain: "Rain",
      snow: "Snow",
      storm: "Storm",
      other: "Other"
    },
    crew: {
      label: "Tasks",
      addCrew: "+ Add Crew",
      subcontractor: "Subcontractor",
      subcontractorPlaceholder: "Select subcontractor",
      workerCount: "Number of workers",
      notes: "Notes (optional)",
      remove: "Remove",
      empty: "No crew added yet",
      sectionTitle: "CREW ON SITE",
      emptyState: "No subcontractors on site that day",
      addSubcontractor: "+ Add subcontractor",
      addTask: "+ Add task",
      noTasks: "No tasks recorded",
      taskName: "Task name",
      percentage: "%",
      notesLabel: "Notes",
      workerCountLabel: "Worker count",
      subcontractorLabel: "Subcontractor",
      cardHeader: {
        workers: "workers"
      }
    },
    workPerformed: "Work Performed",
    workPerformedPlaceholder: "Describe the work completed today...",
    notes: "Notes",
    notesPlaceholder: "Any additional notes, observations, or concerns...",
    photos: {
      label: "Photos",
      dropzone: "Drop photos here or click to upload",
      limit: "Up to 20 photos · JPEG, PNG, WebP · Max 20MB each",
      caption: "Caption (optional)",
      remove: "Remove photo",
      uploading: "Uploading...",
      uploadingCount: "Uploading {count} photo(s)...",
      compressing: "Compressing...",
      fileTooLarge: "File exceeds 20 MB limit and was skipped.",
      heicNotSupported: "HEIC not supported on this browser, please use JPG."
    },
    kpi: {
      totalLogs: "Total Logs",
      missingLogs: "Missing Logs (30d)",
      awaitingApproval: "Awaiting Approval"
    },
    table: {
      date: "Date",
      weather: "Weather",
      team: "Team",
      materials: "Materials",
      issues: "Issues",
      trades: "Trades",
      workers: "Workers",
      photos: "Photos",
      notes: "Notes",
      summary: "Summary",
      author: "Author",
      approval: "Approval",
      actions: "Actions",
      edited: "Edited",
      noLogs: "No daily logs match the current filters"
    },
    filters: {
      dateFrom: "From",
      dateTo: "To",
      weather: "Weather",
      author: "Author",
      allWeather: "All Weather",
      allAuthors: "All Authors",
      approval: "Approval",
      allApproval: "All Approval States",
      clear: "Clear filters"
    },
    actions: {
      view: "View",
      edit: "Edit",
      delete: "Delete"
    },
    approval: {
      submitted: "Submitted",
      approved: "Approved",
      markApproved: "Mark as Approved",
      markSubmitted: "Mark as Submitted",
      toastApproved: "Log approved",
      toastUnapproved: "Approval removed"
    },
    view: {
      title: "Daily Log",
      weather: "Weather",
      crew: "Crew on Site",
      crewTotal: "Total: {trades} trade(s), {workers} worker(s)",
      workPerformed: "Work Performed",
      notes: "Notes",
      photos: "Photos ({count})",
      createdBy: "Created by {author} on {date}",
      editedBy: "Last edited by {editor} on {date}",
      approvedBy: "Approved by {approver} on {date}",
      awaitingApproval: "Awaiting approval",
      close: "Close",
      exportPdf: "Export PDF",
      edit: "Edit"
    },
    bulk: {
      countSelected: "{count} selected",
      approve: "Approve selection",
      approving: "Approving...",
      approveResult: "{approved} approved, {skipped} skipped",
      approveFailed: "{failed} failed",
      downloadPdf: "Download PDF",
      cancel: "Cancel",
      downloading: "Generating PDFs...",
      success: "{count} log(s) downloaded",
      error: "Failed to download. Try again."
    },
    sections: {
      generalInfo: "General Info",
      siteConditions: "Site Conditions",
      crew: "Crew on Site",
      workPerformed: "Work Performed",
      materialsDelivered: "Materials Delivered",
      issues: "Issues",
      notes: "General Notes",
      photos: "Photos"
    },
    preview: {
      noSubcontractor: "No subcontractor",
      noIssue: "No issue",
      noPhoto: "No photo",
      subcontractors: "subcontractors",
      workers: "workers",
      tasks: "tasks"
    },
    picker: {
      empty: "No subcontractor in project directory",
      addToRepertoire: "+ Add to project directory"
    },
    status: {
      draft: "Draft",
      submitted: "Submitted",
      approved: "Approved"
    },
    carriedFromPrevious: "carried from previous log",
    previousPercent: "Previous: {n}%",
    cannotReduce: "Cannot reduce progress. Minimum: {n}%",
    taskCompleted: "Completed",
    locked: {
      task: "Task locked (progress started)",
      subcontractor: "Subcontractor locked (progress started)"
    },
    validation: {
      title: "Cannot save",
      errorTitle: "Cannot save",
      noTask: "{subName}: must have at least 1 task",
      invalidPct: "{subName} → {taskName}: invalid percentage",
      close: "Close",
      goToTask: "Go to task"
    }
  },
  brain: {
    title: "Brain",
    subtitle: "What deserves your attention today.",
    selectorLabel: "Project",
    selectorPlaceholder: "All projects…",
    kpiSignals: "Signals",
    kpiCritical: "Critical",
    kpiExposed: "Money exposed",
    kpiSites: "Sites affected",
    empty: "Nothing to report.",
    blindTitle: "What Brain cannot assess",
    columns: {
      finances: "Finances",
      financesSub: "budget, contracts, extras, credits",
      legal: "Legal",
      legalSub: "what must be signed",
      documents: "Documents",
      documentsSub: "what must be produced, filled or closed"
    },
    expand: "Expand",
    collapse: "Collapse",
    pathTitle: "The path",
    whyTitle: "Why it matters",
    steps: {
      budgetLine: "View the budget line",
      contract: "View the contract",
      contracts: "View contracts",
      project: "View the project",
      dailyLog: "View the daily log",
      extras: "View extras & credits",
      changeOrders: "View change orders",
      subInvoices: "View subcontractor invoicing",
      denonciation: "View the notice of contract",
      quittances: "View the lien waivers",
      clientInvoices: "View client invoicing"
    },
    themes: {
      money: "Money",
      legal: "Legal",
      engage: "Commitment",
      field: "Field"
    },
    signals: {
      stalled_long: "{task} has been at {pct}% for {days} days. {company} has changed nothing across {logs} consecutive logs.",
      near_complete_stalled: "{task} is at {pct}% — nearly done — and hasn't moved in {days} days. Everything downstream is waiting.",
      stalled: "{task} hasn't advanced across {logs} logs. Last movement on {date}.",
      never_captured: "This site has been open for {expected} working days and holds no log at all. If a claim comes, nothing proves what happened.",
      low_coverage: "{logs} logs for {expected} working days — {pct}% of the site is documented. {missing} days left no trace.",
      partial_coverage: "{pct}% of working days are documented. {missing} days are missing.",
      invoiced_no_co: "{amount} is approved for payment on this extra and no change order exists. Nothing is enforceable if the subcontractor disputes it.",
      invoiced_no_co_unapproved: "{amount} is invoiced on an extra that was never approved, and no change order exists.",
      co_unsigned: "{amount} invoiced. The change order exists but nobody signed it.",
      co_draft: "{amount} invoiced. The change order is still a draft — nobody sent it for signature.",
      co_sent: "{amount} invoiced. The change order went out for signature and never came back.",
      unassigned_site_running: "{poste} is budgeted at {budget} and has no subcontractor. The site has been running for {days} days.",
      unassigned_site_started: "{poste} is budgeted at {budget} and has no subcontractor. The site has just started.",
      unassigned_site_imminent: "{poste} is budgeted at {budget} and has no subcontractor. The site starts in {daysUntil} days.",
      unsigned_awaiting_signature_long: "{company} has been awaiting signature for {daysContract} days. The site has been open {daysSite} days.",
      unsigned_contract_stale: "{company} received this contract {daysContract} days ago. Still unsigned.",
      unsigned_site_long_running: "The site has been running {daysSite} days and {company}'s contract is still open.",
      unsigned_site_running: "{company}'s contract is unsigned. Site open for {daysSite} days.",
      unsigned_site_started: "{company}'s contract is not signed yet and the site has just started.",
      over_budget_preventable: "{company} is awarded at {value} against a budget line of {budget} — {pct}%. The contract is unsigned: this amount deserves a second look.",
      over_budget_committed_large: "{company} was signed at {value} against a budget line of {budget}. {overage} more than planned, already committed.",
      over_budget_committed: "{company} exceeds its budget line by {overage} — {pct}% of budget.",
      over_budget_marginal: "{company} slightly exceeds its budget line, by {overage}.",
      tm_no_budget_ceiling: "{companies} works time & material on {poste}, which carries no budget. There is no ceiling to measure spending against.",
      tm_budget_exceeded: "The {poste} budget is exceeded. {companies} works time & material on it — the line sits between {pctMin}% and {pctMax}% of budget.",
      tm_budget_at_risk: "{poste} can reach {pctMax}% of its budget. {companies} works time & material on it, with no contractual ceiling.",
      tm_budget_approaching: "{poste} is approaching its budget — up to {pctMax}%. {companies} works time & material on it.",
      tm_under_watch: "{companies} works time & material on {poste}. The line sits at {pctMax}% of its {budget} budget. Watch stays active until the final release.",
      paid_unreleased_denounced: "{uncovered} was paid to {company} with no signed release, and a notice of claim already exists on this contract.",
      paid_unreleased_large: "{uncovered} was paid to {company} and no release ever came back.",
      paid_unreleased: "{uncovered} paid to {company}, no release on file.",
      paid_unreleased_small: "{uncovered} paid to {company} with no release. Small amount, but the file stays open.",
      released_without_payment: "A release of {released} is signed for {company}, yet no payment was ever recorded.",
      approved_before_client_payment: "Invoice approved before client payment: PR-{dp} · {status} · {amount}."
    },
    silence: {
      not_enough_logs: "Not enough logs on this site to establish a rhythm.",
      not_started: "The site hasn't started yet.",
      no_working_days: "No working day has elapsed.",
      no_start_date: "No start date is recorded.",
      site_not_started: "The site hasn't started — an unsigned contract is normal.",
      no_budget_line: "This contract isn't attached to any budget line.",
      no_budget_amount: "The budget line carries no amount.",
      no_contract_value: "The contract carries no value.",
      final_quittance_signed: "The final release is signed — the watch is lifted.",
      no_ceiling: "No budget on this line: there is no ceiling.",
      budget_fully_committed: "The budget is already fully committed by contract.",
      nothing_paid: "Nothing was paid to this subcontractor — there is nothing to release.",
      fully_released: "Everything paid is covered by a signed release."
    },
    why: {
      stalled_long: "A task sitting untouched for weeks is no longer an oversight, it is a commitment nobody picked back up. The longer it ages, the less the subcontractor remembers what was agreed.",
      near_complete_stalled: "A nearly finished task that stopped moving usually costs less than two hours of work. This is the kind of leftover that delays a site handover over something trivial.",
      stalled: "A task with no movement for several days means either a blocker nobody named, or work the field completed without closing it out. Both are fixed with one question to the superintendent.",
      never_captured: "A site with no daily log leaves no record of what happened. The day a delay or a damage is disputed, all that remains is each side's word.",
      low_coverage: "A handful of logs across several weeks cannot reconstruct a sequence of work. A claim is defended with consecutive days, not with samples.",
      partial_coverage: "The missing days are almost always the ones where something abnormal happened. Filling the gaps while the field still remembers takes minutes; doing it six months later is impossible.",
      invoiced_no_co: "An extra invoiced without a change order is work the client never formally authorized at that price. It is the first amount a client disputes, and the hardest one to defend.",
      invoiced_no_co_unapproved: "The change order exists but nobody approved it: the invoice goes out backed by a document with no standing. Until approval lands, the amount is a request, not a receivable.",
      co_unsigned: "An approved but unsigned change order binds nobody yet. The signature is what turns a verbal understanding into a collectible amount.",
      co_draft: "A change order left in draft never left your desk. The client still does not know this amount is being added to their contract, and the longer the gap, the worse the announcement lands.",
      co_sent: "A change order sent with no reply is silence, not agreement. Following up now costs an email; waiting until closeout costs a negotiation.",
      unassigned_site_running: "Costs with no budget line report to nowhere: the project looks profitable because part of the cost is invisible. On a running site, the gap grows every week.",
      unassigned_site_started: "The site just started and amounts are already moving outside the budget. This is the cheapest moment to attach them — later, nobody remembers what they belonged to.",
      unassigned_site_imminent: "Commitments are being made before work even begins, with no anchor in the budget. Starting from an incomplete budget means measuring profitability against a false target.",
      unsigned_awaiting_signature_long: "A contract awaiting signature for a long time signals a hesitation nobody addressed. The subcontractor may already be working, with no enforceable price or obligations.",
      unsigned_contract_stale: "A contract left unsigned for weeks often no longer reflects what is happening on site. It will have to be reopened before signing, and that renegotiation starts from a weak position.",
      unsigned_site_long_running: "The site has been running for a long time with no signed contract. Every week of work performed without a document makes a price or scope dispute harder to settle.",
      unsigned_site_running: "The subcontractor is on site and nothing is signed. In case of default, damage or delay, you have no text to hold them to.",
      unsigned_site_started: "Work has just begun with no signature. This is the last window where the subcontractor signs without arguing, because they have nothing to lose yet.",
      over_budget_preventable: "The overrun has not been spent yet: there is still room to correct it. Once the work is performed, all that is left is to record it.",
      over_budget_committed_large: "A significant overrun is already committed by contract. That amount can no longer be recovered on site — only through a client change order, if it can be justified.",
      over_budget_committed: "The commitment exceeds the budget: the loss is already decided, even if it is not yet invoiced. Flagging it early allows offsetting it elsewhere in the project.",
      over_budget_marginal: "The gap is small but real. Across a project, five marginal gaps equal one major overrun, and none of them were discussed.",
      tm_no_budget_ceiling: "Time-and-material work with no ceiling has no natural limit: the cost follows the hours, not an agreement. The ceiling is the only tool to stop the drift before the invoice.",
      tm_budget_exceeded: "The agreed ceiling is exceeded and the hours keep running. Everything added now is billed without cover, unless there is a written agreement.",
      tm_budget_at_risk: "The T&M work is approaching its ceiling while the work is not finished. This is the last moment to renegotiate before spending without authorization.",
      tm_budget_approaching: "Consumption is approaching the ceiling. Notifying the client now is part of the agreement; notifying them after the overrun looks like a surprise invoice.",
      tm_under_watch: "The T&M work is progressing normally, but it deserves an eye. Time-and-material work rarely drifts all at once — it drifts one week at a time.",
      paid_unreleased_denounced: "A notice of claim turns a theoretical risk into a named one: someone has already stepped forward. Without a release, nothing proves the money paid was passed on, and a legal hypothec can follow.",
      paid_unreleased_large: "The money is out. If the subcontractor doesn't pay their own suppliers, those suppliers can register a legal hypothec on the client's building — and the company pays twice.",
      paid_unreleased: "The release is the only proof the subcontractor acknowledges being paid. Without it, the payment exists only in bank statements.",
      paid_unreleased_small: "Small amount, same principle: money went out with no signed counterpart. This is the kind of file nobody remembers until the day of a claim.",
      released_without_payment: "A release carries an amount larger than what was actually paid. The subcontractor will dispute it, and they will be right. That figure likely came from approved billing rather than from payments.",
      approved_before_client_payment: "The subcontractor is approved for payment while the client has not yet paid this payment request: the company is advancing the money."
    }
  },
  quittances: {
    title: "Liens & Releases",
    subtitle: "Track subcontractor notices and payment releases",
    sidebarLabel: "Notices & Releases",
    tabs: {
      denonciations: "Notices",
      quittances: "Releases"
    },
    kpi: {
      subcontractors: "Subcontractors",
      denounced: "Notified",
      approvedToDate: "Approved billing",
      balance: "Balance to release",
      blocked: "Blocked contracts",
    },
    toolbar: {
      searchPlaceholder: "Search a subcontractor...",
      filterAll: "All",
      filterNotified: "Notified",
      filterNotNotified: "Not notified",
      filterSettled: "Settled",
      newDenonciation: "New notice",
      editDenonciation: "Edit notice",
      subcontractLink: "Linked subcontract",
      subcontractLinkPlaceholder: "Select a subcontract...",
      intervenantName: "Party",
      intervenantNamePlaceholder: "Company or supplier name",
      supplierName: "Supplier",
      supplierNamePlaceholder: "Enter the supplier's name",
      specialitePlaceholder: "e.g. Electrical",
      typeLabel: "Who is giving notice?",
      typePrincipal: "Subcontractor",
      typeSecondaire: "Subcontractor's supplier",
      typeOuverte: "Open supply",
      contractContextLabel: "Selected contract",
      pricingFixed: "Fixed-price contract",
      pricingMaterialSupply: "Supply purchase order",
      pricingLaborMaterial: "Time & material",
      noAmount: "no amount",
      ouverteHint: "Open supply — the amount may be unknown.",
      montantEstimeLabel: "Estimated amount",
      montantOptional: "(optional)",
      montantRequired: "The denounced amount is required for a fixed-amount notice.",
      lieAPlaceholder: "Who they contracted with",
      lieALockedHint: "Your organization's legal name. Change it in Settings > Organization.",
      contractTargeted: "Contract concerned",
      contractTargetedPlaceholder: "Select the supplied subcontractor's contract...",
      contractRequired: "The contract is required.",
      lieAFromContractHint: "Legal name of the selected contract. Follows the contract.",
      dateLabel: "Notice date",
      montantLabel: "Amount notified",
      notesLabel: "Notes",
      notesPlaceholder: "Optional",
      docsLabel: "Documents",
      docsNone: "No document",
      docsAdd: "Attach a document",
      docsOpen: "Open document",
      docsRemove: "Remove document",
      docsRemoveTitle: "Remove document",
      docsRemoveMessage: "This document will be permanently deleted.",
      docsAdded: "Document attached",
      docsRemoved: "Document removed",
      docsPendingHint: "{count} file(s) pending — they will be uploaded on save.",
      docsUploadFailed: "Notice saved, but these files could not be uploaded: {names}",
      lettreLabel: "Notice letter",
      lettreHint: "PDF, JPG or PNG — max 10 MB",
      lettreChoose: "Choose a file",
      lettreReplace: "Replace",
      lettreRemove: "Remove",
      lettreNone: "No letter",
      lettreOpen: "Open letter",
      lettreInvalidType: "Unsupported format. Use PDF, JPG or PNG.",
      lettreTooLarge: "File is too large (max 10 MB).",
      lettreUploadFailed: "The notice was saved, but the letter could not be uploaded.",
      save: "Save",
      required: "Party name is required.",
      createdToast: "Notice added",
      updatedToast: "Notice updated",
      deletedToast: "Notice deleted",
      errorToast: "Something went wrong. Please try again.",
      deleteTitle: "Delete notice",
      deleteMessage: "This notice will be permanently deleted.",
      deleteChecking: "Checking links…",
      deleteBlockedTitle: "Cannot delete",
      deleteBlockedMessage: "This notice is covered by a release. Remove the release first to delete it.",
      deleteBlockedClose: "Close",
      blockedQuittances: "{count} release(s)",
      deleteAlsoRemoves: "{count} attached document(s) will also be deleted.",
      edit: "Edit",
      delete: "Delete",
      actions: "Actions"
    },
    table: {
      subcontractor: "Subcontractor",
      dateDenonciation: "Notice date",
      notNotified: "— not notified",
      revised: "Revised amount",
      balance: "Balance",
      status: "Status",
      dp: "Release DP{number}",
      noDp: "No PR",
      taxIncl: "taxes incl.",
      beforeTax: "before tax",
      cumulative: "cumulative",
      toRelease: "to release",
      released: "released",
      planned: "to plan",
      cumulAboveRelease: "Cumulative paid exceeds the signed release",
      remaining: "remaining",
      laborMaterial: "Time & material",
      letter: "Letter attached",
      details: {
        paidInvoices: "Paid invoices",
        approvedInvoices: "Approved invoices",
        previousDps: "Already covered by DP{from}–DP{to}",
        previousDp: "Already covered by DP{number}",
        remaining: "{remaining} left of {total}",
        noNumber: "No number",
        total: "Total"
      }
    },
    status: {
      toNotify: "To notify",
      partial: "Partial",
      settled: "Settled",
      settledFinale: "Settled — final issued",
      finaleUnbalanced: "Final issued — balance not zero",
    },
    emit: {
      column: "Releases",
      chipNumbered: "Release {number}",
      dpOption: "PR {number}",
      chipFinale: "Final",
      action: "Issue",
      title: "Issue a release",
      dpLabel: "Payment request",
      typeLabel: "Release type",
      type: {
        partielle: "Partial",
        finale: "Final"
      },
      amountLabel: "Amount (taxes incl.)",
      issuerLabel: "Who is issuing this release?",
      issuerSubcontractor: "{name} — subcontractor",
      issuerSupplier: "{name} — denounced supplier",
      supplierRecap: "Denounced {denounced} · released {released} · remaining {remaining}",
      supplierAmountRequired: "Enter the amount of the release received.",
      amountRequired: "No approved invoice to release for this selection.",
      amountFromDp: "Total paid through PR {number}",
      amountFromAll: "Total paid to date, all payment requests",
      dpNoNewInvoice: "No new approved invoice for this period.",
      finaleNeedsBalance: "{amount} remains uninvoiced. Invoice the balance, or issue a credit change order to bring the contract to its real amount.",
      balanceUnverifiable: "Contract with no reference amount — the balance cannot be verified.",
      finaleLocksDp: "Final — settles the whole contract, attached to the chosen payment request",
      linkedToNotice: "Linked to the notice dated {date}",
      linkedToNoticeNoDate: "Linked to this contract's notice",
      dateLabel: "Release date",
      legalNotice: "Attach the release signed by the subcontractor. OnsitePM generates no form — use your own.",
      confirm: "Record the release",
      dupDp: "A release already exists for this subcontractor and this payment request.",
      dupFinale: "A final release has already been issued for this subcontractor.",
      dupFinaleSupplier: "A final release has already been recorded for this supplier.",
      openExisting: "Open the existing release",
      dpCovered: "Release received",
      createdToast: "Release recorded",
      deletedToast: "Release deleted",
      deleteTitle: "Delete this release?",
      deleteMessage: "The release will be permanently removed from the file. This cannot be undone."
    },
    exposure: {
      title: "Suppliers denounced under this contract",
      explain: "Paying this subcontractor without holding its suppliers' releases exposes the jobsite to a legal hypothec from them.",
      denounced: "Denounced",
      released: "Released",
      unknownAmount: "amount unknown",
      seeNotice: "View the notice",
      markerTooltip: "{name} — denounced {denounced}, remaining {remaining}",
      status: {
        uncovered: "No release",
        partial: "Partial",
        covered: "Released"
      }
    },
    supplier: {
      savedButUploadFailed: "Release recorded, but the file {name} could not be uploaded."
    },
    signed: {
      title: "Signed release",
      attach: "Attach the signed release",
      present: "Signed release attached — click to open or replace",
      hint: "PDF, JPG or PNG · 10 MB max · single file",
      replaceHint: "One signed release per issuance. To replace it, remove the current file then drop the new one.",
      uploadedToast: "Signed release attached",
      removedToast: "Signed release removed",
      removeTitle: "Remove the signed release?",
      removeMessage: "The signed file will be detached from this release. The release itself is kept."
    },
    denonciations: {
      intervenant: "Party",
      specialite: "Specialty",
      lieA: "Contract with",
      date: "Notice date",
      montant: "Amount notified",
      letter: "Letter",
      principal: "Principal",
      secondaire: "Secondary",
      estime: "est.",
      empty: "No notice",
      emptyHint: "Notices received for this project will appear here."
    },
    empty: {
      noSubcontracts: "No subcontract",
      noSubcontractsHint: "Add contracts to this project to track releases."
    }
  },
  rfi: {
    saved: "RFI saved",
    deleted: "RFI deleted",
    deleteFailed: "The RFI could not be deleted.",
    saveFailed: "The RFI was not saved. Try again.",
    deleteLocked: "An answered RFI cannot be deleted.",
    viewTitle: "View RFI",
    editTitle: "Edit RFI",
    deleteDialog: {
      title: "Delete RFI",
      message: "Are you sure you want to delete this RFI? This action cannot be undone.",
      delete: "Delete",
      cancel: "Cancel"
    },
    actions: {
      view: "View",
      downloadPdf: "Download PDF",
      delete: "Delete",
      respond: "Respond"
    },
    status: {
      draft: "Draft",
      sent_for_approval: "Sent for approval",
      answered: "Answered",
      in_review: "In review"
    },
    qrt: {
      title: "Answer to RFI {number}",
      warning: "Once submitted, this RFI will be marked as answered.",
      editWarning: "This response will be replaced. There is only one response per RFI, with no history.",
      responsePdfOptional: "Response PDF",
      pdfAttachedDocument: "Attached document",
      minCharacters: "Minimum 10 characters required.",
      photosDropzone: "Click to add photos",
      photosFormats: "JPEG, PNG, WebP",
      submitting: "Submitting...",
      errorFallback: "Failed to submit response.",
      pdfDropzone: "Drag and drop or click to upload",
      pdfDropzoneHint: "PDF only · max 50 MB",
      pdfReplaceDropzone: "Drag and drop or click to replace the document",
      pdfReplaceHelper: "The current document will be replaced.",
      responseText: "Response text",
      responseTextPlaceholder: "Paste the response from email...",
      responsePdf: "Response PDF (required)",
      responsePhotos: "Response photos (optional)",
      submit: "Submit response",
      cancel: "Cancel",
      success: "Response submitted successfully",
      errorPrefix: "Submission error"
    },
    pageTitle: "RFI",
    editResponse: "Edit response",
    locked: "Locked",
    question: "Question",
    response: "Response",
    answeredAt: "Answered on",
    downloadResponsePdf: "Download response PDF",
    statusUpdated: "Status updated",
    subtitle: "Manage and track Requests for Information",
    professions: {
      architect: "Architect",
      mechanical: "Mechanical",
      electrical: "Electrical",
      structural: "Structural",
      civil: "Civil",
      plumbing: "Plumbing",
      generalContractor: "General Contractor",
      owner: "Owner",
      other: "Other"
    },
    kpi: {
      totalRfis: "Total"
    },
    toolbar: {
      searchPlaceholder: "Search RFIs...",
      filterAll: "All",
      newRfi: "New RFI"
    },
    table: {
      number: "RFI #",
      subject: "Subject",
      profession: "Profession",
      photos: "Photos",
      status: "Status",
      date: "Date",
      actions: "Actions"
    },
    empty: {
      noRfis: "No RFIs found",
      createFirst: "Create your first RFI to get started",
      noneOpen: "No open RFI ✓"
    },
    create: {
      title: "New RFI",
      subtitle: "Request for Information",
      subjectLabel: "Subject",
      subjectPlaceholder: "e.g. Clarification on structural beam dimensions",
      professionLabel: "Profession",
      professionPlaceholder: "Select profession...",
      questionLabel: "Question",
      questionPlaceholder: "Describe your question or request in detail...",
      photosLabel: "Photos",
      uploadHint: "Click to upload photos",
      uploadFormats: "JPEG, PNG only",
      photoPreviewAlt: "Preview {index}",
      submit: "Create RFI",
      submitting: "Creating...",
      subjectRequired: "Subject is required.",
      createFailed: "Failed to create RFI."
    },
    detail: {
      professionLabel: "Profession",
      dateLabel: "Date",
      statusLabel: "Status",
      photosCount: "Photos ({count})",
      responsePhotoAlt: "Response photo {index}",
      generatePdf: "Generate PDF",
      generatingPdf: "Generating...",
      noPhotos: "No attached photos",
      photoDeleteFailed: "The photo could not be deleted."
    }
  },
  projectSchedule: {
    title: "Project Schedule",
    today: "Today",
    subtitle: "Timeline view of all active projects",
    granularity: {
      day: "Day",
      week: "Week",
      month: "Month",
      quarter: "Quarter"
    },
    nav: {
      today: "Today"
    },
    filter: {
      allPMs: "All PMs"
    },
    legend: {
      title: "Project Managers"
    },
    empty: "No active projects to display",
    tooltip: {
      pm: "PM",
      duration: "Duration",
      days: "days",
      value: "Contract Value",
      status: "Status"
    }
  },
  orgSettings: {
    pageTitle: "Organization Settings",
    pageSubtitle: "Manage your organization\'s configuration.",
    navLabel: "Org Settings",
    accessDenied: {
      title: "Admin Access Required",
      message: "This page is reserved for administrators.",
      goHome: "Go to Dashboard"
    },
    templates: {
      sectionTitle: "Permission templates",
      sectionSubtitle: "Each template decides which pages its holders may open.",
      templateCount: "template(s)",
      systemBadge: "System",
      usersCount: "{count} user(s)",
      noneYet: "No template yet.",
      nameLabel: "Template name",
      namePlaceholder: "e.g. Project manager",
      nameRequired: "Name is required.",
      newTemplate: "New template",
      duplicate: "Duplicate",
      duplicateSuffix: "{name} (copy)",
      rename: "Rename",
      delete: "Delete",
      save: "Save",
      saved: "Template saved",
      created: "Template created",
      deleted: "Template deleted",
      cancel: "Cancel",
      edit: "Edit",
      selectAll: "Select all",
      selectNone: "Clear all",
      reset: "Reset",
      resetConfirmTitle: "Reset this template?",
      resetConfirmMessage: "\u201C{name}\u201D will return to its original configuration. Changes made since will be lost.",
      resetDone: "Template reset",
      groupApp: "General pages",
      groupProject: "Project pages",
      upcoming: "upcoming",
      hiddenPage: "reached from another page",
      systemEditable: "System templates can be edited but not deleted. Each organization has its own copies.",
      deleteConfirmTitle: "Delete this template?",
      deleteConfirmMessage: "\u201C{name}\u201D will be permanently deleted.",
      blocked: {
        system_template: "A system template cannot be deleted. You can edit it.",
        in_use: "\u201C{name}\u201D is still assigned to {count} user(s). Reassign them before deleting it.",
        last_admin_users_page: "Not possible: this template belongs to the last active admin. Removing \u201CUsers\u201D would close user management for them, with no way back."
      },
      error: "The operation failed. Please try again.",
      pages: {
        appProjectList: "Project list",
        appSchedule: "Project schedule",
        appDirectory: "Directory",
        appInvoiceInbox: "Invoice inbox",
        appProjectView: "Dashboard",
        appBrain: "Brain",
        appOnx: "OnX (AI Assistant)",
        appMargin: "Margin",
        appUsers: "Users",
        appOrgSettings: "Organization settings",
        projectDirectory: "Project directory",
        projectDocuments: "Documents",
        projectSchedule: "Schedule",
        projectDailyLog: "Daily log",
        projectRfi: "RFI",
        projectBudget: "Financial overview",
        projectContracts: "Contracts",
        projectExtras: "Directives & Extras",
        projectChangeOrders: "Change orders",
        projectExpenses: "Expenses",
        projectSubInvoices: "Received invoices",
        projectSubInvoiceApprove: "Invoice approval",
        projectClientInvoices: "Client invoicing",
        projectQuittances: "Notices & Releases"
      }
    },
    trades: {
      sectionTitle: "Trades",
      sectionSubtitle: "Configure the list of trades available when creating subcontractors.",
      activeCount: "active",
      addPlaceholder: "New trade name...",
      active: "Active",
      inactive: "Inactive",
      activate: "Activate",
      deactivate: "Deactivate",
      moveUp: "Move up",
      moveDown: "Move down",
      empty: "No trades configured yet.",
      loadError: "Failed to load trades.",
      writeError: "Failed to save changes. You may not have admin permissions.",
      retry: "Retry",
      deleteConfirmTitle: "Delete Trade",
      deleteConfirmMessage: "Are you sure you want to delete \"{name}\"? This action cannot be undone."
    }
  },
  project360: {
    title: "Dashboard",
    subtitle: "Project overview — profitability, billing and costs.",
    selectorLabel: "Project",
    selectorPlaceholder: "Select a project…",
    breadcrumbPortfolio: "Portfolio",
    anchorProfitParDp: "Profit par DP",
    kpiContrat: "Contract",
    kpiBilled: "Billed work",
    kpiCosts: "Costs",
    kpiMargin: "Margin $",
    kpiMarginPct: "Margin %",
    teamPm: "PM",
    teamSupervisor: "Surint.",
    teamCoordinator: "Coord.",
    cardProfitParDp: "Profit per payment request",
    cardFacturation: "Billing & Holdbacks",
    cardFacturesST: "ST Invoices to Pay",
    anchorFacturation: "Billing & Holdbacks",
    anchorFacturesST: "ST Invoices to Pay",
    statusApproved: "Approved",
    statusSubmitted: "Submitted",
    statusDraft: "Draft",
    noDps: "No payment requests found.",
    unassignedNote: "Costs not linked to a DP: {amount}",
    noData: "Project not found.",
    enAttenteApprobation: "Pending approval",
    retenueAccumulee: "Accumulated holdback",
    colDp: "DP",
    colSousTotal: "Subtotal",
    colFacture: "Billed",
    colRetenue: "Holdback",
    colStatut: "Status",
    colSousTraitant: "Subcontractor",
    colSolde: "Balance",
    colAge: "Age",
    totalDu: "Total due",
    noOutstandingST: "No outstanding ST invoices.",
    moreDetails: "More details →",
    refreshedAt: "Updated at {time}",
    refreshBtn: "↻ Refresh",
    notesTitle: "Notes",
    notesPlaceholder: "Add a note…",
    notesAdd: "Add",
    notesEmpty: "No notes."
  },
  portfolioProfitability: {
    title: "Portfolio Profitability",
    subtitle: "Cross-project revenue, costs and margin overview.",
    filterAll: "All",
    filterActive: "Active only",
    kpiContract: "Total Contract",
    kpiBilled: "Billed (client)",
    kpiCosts: "Linked Costs",
    kpiMarginAmt: "Margin $",
    kpiMarginPct: "Margin %",
    chartTitle: "Margin % by project",
    colProject: "Project",
    colStatus: "Status",
    colContract: "Contract",
    colBilledCosts: "Billed / Costs",
    colBilled: "Billed",
    colCosts: "Costs",
    colMarginAmt: "Margin $",
    colMarginPct: "Margin %",
    total: "Total",
    legendBilled: "Billed (client)",
    legendCosts: "Costs",
    unassignedNote: "Costs not linked to a payment request: {amount}",
    noData: "No projects found."
  },
  onx: {
    greeting: "Hi! 👋 How can I help you today?",
    placeholder: "Write to OnX...",
    newChat: "New chat",
    online: "Online",
    subtitle: "AI Assistant · OnSiteX",
    typing: "OnX is thinking...",
    comingSoon: "Coming soon",
    errorGeneric: "An error occurred. Please try again.",
    send: "Send"
  },
  pilot: {
    title: "Pilot",
    emptyTitle: "Ask a question about your project",
    emptyTitleGlobal: "Ask a question about your projects",
    emptySubtitle: "Pilot queries your data in real time.",
    emptySubtitleGlobal: "Pilot can search across all your authorized projects.",
    inputPlaceholder: "Ask a question…",
    noProject: "Select a project to use Pilot.",
    errorGeneric: "Something went wrong. Please try again.",
    toolsUsed: "Data consulted",
    newConversation: "New conversation",
    contextOrg: "Organizational context"
  }
};
